// ============================================================
// AGRONOTAS - Aplicativo Mobile NFe
// Padrão MVC | React | Verde & Branco
// ============================================================

import { useState, useEffect, useCallback, useRef } from "react";

// ============================================================
// MODEL - API & Data Layer
// ============================================================
const EMPRESA_ID = 5;

const ApiModel = {
  _token: null,
  _empresaId: EMPRESA_ID,
  _baseUrl: "https://ws-hml.cgcom.inf.br/ws",

  setToken(token) { this._token = token; },
  getToken() { return this._token; },
  setBaseUrl(url) { this._baseUrl = url; },

  headers() {
    return {
      "Content-Type": "application/json",
      Authorization: this._token || "",
      EmpresaId: String(this._empresaId),
    };
  },

  async login(username, password) {
    const res = await fetch(`${this._baseUrl}/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password }),
    });
    if (!res.ok) throw new Error("Usuário ou senha inválidos");
    // Token comes in the Authorization response header
    const token = res.headers.get("Authorization") || "";
    if (!token) throw new Error("Token não retornado pelo servidor");
    this.setToken(token);
    return token;
  },

  async get(path) {
    const res = await fetch(`${this._baseUrl}${path}`, { headers: this.headers() });
    if (!res.ok) throw new Error(`Erro ao buscar dados (${res.status})`);
    return res.json();
  },

  async post(path, body) {
    const res = await fetch(`${this._baseUrl}${path}`, {
      method: "POST", headers: this.headers(), body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`Erro ao salvar (${res.status})`);
    return res.json();
  },

  async put(path, body) {
    const res = await fetch(`${this._baseUrl}${path}`, {
      method: "PUT", headers: this.headers(), body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`Erro ao atualizar (${res.status})`);
    return res.json();
  },

  async del(path) {
    const res = await fetch(`${this._baseUrl}${path}`, {
      method: "DELETE", headers: this.headers(),
    });
    if (!res.ok) throw new Error(`Erro ao excluir (${res.status})`);
    return true;
  },

  // Recursos da API
  pessoas: {
    listar: () => ApiModel.get("/pessoas"),
    buscarPorId: (id) => ApiModel.get(`/pessoas/${id}`),
    buscarPorCpfCnpj: (doc) => ApiModel.get(`/pessoas/cpfcnpj/${doc}`),
    cadastrar: (data) => ApiModel.post("/pessoas", { ...data, id: null }),
    alterar: (data) => ApiModel.put("/pessoas", data),
    excluir: (id) => ApiModel.del(`/pessoas/${id}`),
  },
  produtos: {
    listar: () => ApiModel.get("/produtos"),
    buscarPorId: (id) => ApiModel.get(`/produtos/${id}`),
    cadastrar: (data) => ApiModel.post("/produtos", { ...data, id: null }),
    alterar: (data) => ApiModel.put("/produtos", data),
    excluir: (id) => ApiModel.del(`/produtos/${id}`),
  },
  nfe: {
    listar: () => ApiModel.get("/nfe"),
    cadastrar: (data) => ApiModel.post("/nfe", { ...data, id: null }),
    alterar: (data) => ApiModel.put("/nfe", data),
    excluir: (id) => ApiModel.del(`/nfe/${id}`),
    emitir: (id) => ApiModel.post(`/nfe/emite/${id}`, {}),
    imprimir: (id) => ApiModel.post(`/nfe/imprime/${id}`, {}),
  },
  ncms: {
    listar: () => ApiModel.get("/ncms"),
  },
  unidades: {
    listar: () => ApiModel.get("/unidades"),
  },
};

// ============================================================
// APP CONFIG — ambiente e URL da API (persistido em localStorage)
// ============================================================
const DEVUSER = "cgcom"; // único usuário que pode ativar Homologação

const API_URLS = {
  PRODUCAO:    "https://ws.cgcom.inf.br/ws",
  HOMOLOGACAO: "https://ws-hml.cgcom.inf.br/ws",
};

const AppConfig = {
  _key: "agronotas_config",
  _defaults: { ambiente: "HOMOLOGACAO", apiBase: "https://ws-hml.cgcom.inf.br/ws" },
  load() {
    try {
      const saved = { ...this._defaults, ...JSON.parse(localStorage.getItem(this._key) || "{}") };
      // Migrate old HTTP URLs to HTTPS
      if (saved.apiBase && saved.apiBase.startsWith("http://")) {
        saved.apiBase = saved.apiBase.replace("http://", "https://");
      }
      return saved;
    }
    catch { return { ...this._defaults }; }
  },
  save(cfg) { localStorage.setItem(this._key, JSON.stringify(cfg)); },
};

// ============================================================
// Local storage para pedidos (sem API)
const PedidoModel = {
  _key: "agronotas_pedidos",
  listar() {
    try { return JSON.parse(localStorage.getItem(this._key) || "[]"); } catch { return []; }
  },
  salvar(pedido) {
    const lista = this.listar();
    if (pedido.id) {
      const idx = lista.findIndex(p => p.id === pedido.id);
      if (idx >= 0) lista[idx] = pedido; else lista.push(pedido);
    } else {
      pedido.id = Date.now();
      pedido.data_criacao = new Date().toISOString();
      lista.push(pedido);
    }
    localStorage.setItem(this._key, JSON.stringify(lista));
    return pedido;
  },
  excluir(id) {
    const lista = this.listar().filter(p => p.id !== id);
    localStorage.setItem(this._key, JSON.stringify(lista));
  },
};

// Fake data para formas pagamento e operações fiscais (simulação enquanto API não tem endpoint dedicado)
const FormasPgtoModel = {
  _key: "agronotas_formas_pgto",
  _defaults: [
    { id: 1, descricao: "DINHEIRO" },
    { id: 2, descricao: "CARTÃO CRÉDITO" },
    { id: 3, descricao: "CARTÃO DÉBITO" },
    { id: 4, descricao: "PIX" },
    { id: 5, descricao: "BOLETO" },
    { id: 6, descricao: "TRANSFERÊNCIA" },
  ],
  listar() {
    const saved = localStorage.getItem(this._key);
    return saved ? JSON.parse(saved) : this._defaults;
  },
  salvar(item) {
    const lista = this.listar();
    if (item.id) {
      const idx = lista.findIndex(p => p.id === item.id);
      if (idx >= 0) lista[idx] = item; else lista.push(item);
    } else {
      item.id = Date.now();
      lista.push(item);
    }
    localStorage.setItem(this._key, JSON.stringify(lista));
    return item;
  },
  excluir(id) {
    const lista = this.listar().filter(p => p.id !== id);
    localStorage.setItem(this._key, JSON.stringify(lista));
  },
};

const OperacoesModel = {
  _key: "agronotas_operacoes",
  _defaults: [
    { id: 1, descricao: "VENDA DE MERCADORIA" },
    { id: 2, descricao: "DEVOLUÇÃO DE COMPRA" },
    { id: 3, descricao: "REMESSA" },
    { id: 4, descricao: "RETORNO DE REMESSA" },
    { id: 5, descricao: "TRANSFERÊNCIA" },
    { id: 6, descricao: "VENDA RURAL" },
  ],
  listar() {
    const saved = localStorage.getItem(this._key);
    return saved ? JSON.parse(saved) : this._defaults;
  },
  salvar(item) {
    const lista = this.listar();
    if (item.id) {
      const idx = lista.findIndex(p => p.id === item.id);
      if (idx >= 0) lista[idx] = item; else lista.push(item);
    } else {
      item.id = Date.now();
      lista.push(item);
    }
    localStorage.setItem(this._key, JSON.stringify(lista));
    return item;
  },
  excluir(id) {
    const lista = this.listar().filter(p => p.id !== id);
    localStorage.setItem(this._key, JSON.stringify(lista));
  },
};

// ============================================================
// STYLES INJECTION
// ============================================================
const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

:root {
  --g900: #052e16;
  --g800: #14532d;
  --g700: #15803d;
  --g600: #16a34a;
  --g500: #22c55e;
  --g400: #4ade80;
  --g300: #86efac;
  --g200: #bbf7d0;
  --g100: #dcfce7;
  --g50:  #f0fdf4;
  --white: #ffffff;
  --slate50: #f8fafc;
  --slate100: #f1f5f9;
  --slate200: #e2e8f0;
  --slate300: #cbd5e1;
  --slate400: #94a3b8;
  --slate500: #64748b;
  --slate600: #475569;
  --slate700: #334155;
  --slate800: #1e293b;
  --slate900: #0f172a;
  --red400: #f87171;
  --red500: #ef4444;
  --red100: #fee2e2;
  --amber400: #fbbf24;
  --amber100: #fef3c7;
  --blue500: #3b82f6;
  --blue100: #dbeafe;
}

html, body, #root {
  height: 100%;
  font-family: 'Plus Jakarta Sans', sans-serif;
  background: #e8f5e9;
  -webkit-font-smoothing: antialiased;
  overflow: hidden;
}

/* ---- MOBILE SHELL ---- */
.shell {
  max-width: 430px;
  height: 100vh;
  margin: 0 auto;
  background: var(--slate50);
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  box-shadow: 0 0 60px rgba(0,0,0,0.2);
}

/* ---- LOGIN ---- */
.login-screen {
  height: 100vh;
  background: linear-gradient(175deg, var(--g800) 0%, var(--g600) 55%, var(--g400) 100%);
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
}
.login-circles {
  position: absolute; inset: 0; pointer-events: none; overflow: hidden;
}
.login-circles span {
  position: absolute; border-radius: 50%;
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.08);
}
.login-circles span:nth-child(1) { width:300px;height:300px; top:-80px; right:-80px; }
.login-circles span:nth-child(2) { width:200px;height:200px; top:60px; left:-60px; }
.login-circles span:nth-child(3) { width:150px;height:150px; bottom:200px; right:30px; }

.login-hero {
  flex: 1; display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  padding: 48px 32px 24px; position: relative;
}
.login-icon-wrap {
  width: 88px; height: 88px;
  background: rgba(255,255,255,0.15);
  backdrop-filter: blur(12px);
  border-radius: 28px;
  display: flex; align-items: center; justify-content: center;
  font-size: 42px;
  margin-bottom: 20px;
  border: 1.5px solid rgba(255,255,255,0.25);
  box-shadow: 0 12px 40px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.3);
}
.login-app-name {
  font-size: 34px; font-weight: 800;
  color: white; letter-spacing: -1px;
  text-shadow: 0 2px 12px rgba(0,0,0,0.2);
}
.login-tagline {
  font-size: 14px; color: rgba(255,255,255,0.7);
  margin-top: 6px; font-weight: 400;
}
.login-card {
  background: var(--white);
  border-radius: 28px 28px 0 0;
  padding: 32px 28px 48px;
  box-shadow: 0 -12px 48px rgba(0,0,0,0.15);
  position: relative;
}
.login-card::before {
  content: '';
  display: block; width: 40px; height: 4px;
  background: var(--slate200); border-radius: 2px;
  margin: 0 auto 28px;
}
.login-card h2 { font-size: 22px; font-weight: 700; color: var(--slate900); }
.login-card p { font-size: 14px; color: var(--slate400); margin-top: 4px; margin-bottom: 28px; }

/* ---- TOPBAR ---- */
.topbar {
  background: linear-gradient(135deg, var(--g800), var(--g700));
  padding: 52px 20px 16px;
  color: white;
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
}
.topbar::after {
  content: '';
  position: absolute;
  right: -30px; top: -30px;
  width: 120px; height: 120px;
  border-radius: 50%;
  background: rgba(255,255,255,0.06);
}
.topbar-inner {
  display: flex; align-items: center; justify-content: space-between;
  position: relative; z-index: 1;
}
.topbar-left { display: flex; align-items: center; gap: 12px; }
.topbar-back {
  width: 36px; height: 36px;
  background: rgba(255,255,255,0.15);
  border-radius: 10px;
  border: none; color: white; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  font-size: 18px;
  transition: background 0.15s;
}
.topbar-back:hover { background: rgba(255,255,255,0.25); }
.topbar-title { font-size: 18px; font-weight: 700; }
.topbar-subtitle { font-size: 12px; color: rgba(255,255,255,0.65); margin-top: 1px; }
.topbar-action {
  width: 36px; height: 36px;
  background: rgba(255,255,255,0.15);
  border-radius: 10px;
  border: none; color: white; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  font-size: 18px;
  transition: background 0.15s;
}
.topbar-action:hover { background: rgba(255,255,255,0.25); }

/* ---- BOTTOM NAV ---- */
.bottom-nav {
  display: flex; align-items: stretch;
  background: var(--white);
  border-top: 1px solid var(--slate200);
  padding-bottom: env(safe-area-inset-bottom, 8px);
  flex-shrink: 0;
  box-shadow: 0 -4px 20px rgba(0,0,0,0.07);
}
.nav-item {
  flex: 1; display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  gap: 3px; padding: 10px 4px 8px;
  cursor: pointer; border: none; background: transparent;
  transition: all 0.18s;
  position: relative;
}
.nav-item .nav-icon {
  font-size: 20px;
  transition: transform 0.18s;
}
.nav-item .nav-label {
  font-size: 10px; font-weight: 600;
  color: var(--slate400);
  font-family: 'Plus Jakarta Sans', sans-serif;
  letter-spacing: 0.3px;
  transition: color 0.18s;
}
.nav-item.active .nav-label { color: var(--g700); }
.nav-item.active .nav-icon { transform: translateY(-1px); }
.nav-item.active::after {
  content: ''; position: absolute;
  bottom: 0; left: 50%; transform: translateX(-50%);
  width: 20px; height: 3px;
  background: var(--g600); border-radius: 2px 2px 0 0;
}

/* ---- CONTENT AREA ---- */
.content {
  flex: 1; overflow-y: auto;
  padding-bottom: 8px;
  scroll-behavior: smooth;
}
.content::-webkit-scrollbar { display: none; }

/* ---- DASHBOARD ---- */
.dashboard-header {
  background: linear-gradient(135deg, var(--g800), var(--g600));
  padding: 52px 20px 80px;
  position: relative; overflow: hidden;
}
.dashboard-header::before {
  content: '';
  position: absolute; inset: 0;
  background: radial-gradient(circle at 80% 0%, rgba(255,255,255,0.08) 0%, transparent 60%);
}
.dash-greeting { font-size: 13px; color: rgba(255,255,255,0.65); font-weight: 500; }
.dash-title { font-size: 26px; font-weight: 800; color: white; margin-top: 2px; letter-spacing: -0.5px; }
.dash-date { font-size: 12px; color: rgba(255,255,255,0.5); margin-top: 4px; }

.dash-stats-row {
  display: flex; gap: 12px;
  padding: 0 16px;
  margin-top: -48px;
  position: relative; z-index: 2;
}
.stat-card {
  flex: 1; background: white;
  border-radius: var(--r);
  padding: 14px 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.10);
  text-align: center;
}
.stat-card .stat-icon { font-size: 22px; margin-bottom: 4px; }
.stat-card .stat-val { font-size: 20px; font-weight: 800; color: var(--slate900); }
.stat-card .stat-lbl { font-size: 10px; color: var(--slate400); font-weight: 600; margin-top: 1px; text-transform: uppercase; letter-spacing: 0.4px; }

.section-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 20px 16px 10px;
}
.section-title { font-size: 16px; font-weight: 700; color: var(--slate800); }
.section-link { font-size: 13px; font-weight: 600; color: var(--g700); }

.menu-grid {
  display: grid; grid-template-columns: 1fr 1fr;
  gap: 12px; padding: 4px 16px 16px;
}
.menu-card {
  background: white;
  border-radius: 16px;
  padding: 20px 16px;
  cursor: pointer; border: none;
  display: flex; flex-direction: column;
  align-items: flex-start; gap: 10px;
  box-shadow: var(--shadow);
  transition: all 0.18s;
  position: relative; overflow: hidden;
  text-align: left;
}
.menu-card::before {
  content: ''; position: absolute;
  top: 0; right: 0;
  width: 60px; height: 60px;
  border-radius: 0 16px 0 60px;
  background: var(--g50);
  transition: background 0.18s;
}
.menu-card:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(0,0,0,0.12); }
.menu-card:hover::before { background: var(--g100); }
.menu-card-icon {
  width: 44px; height: 44px;
  border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  font-size: 22px;
}
.menu-card-name { font-size: 14px; font-weight: 700; color: var(--slate800); line-height: 1.3; }
.menu-card-desc { font-size: 11px; color: var(--slate400); font-weight: 500; }

/* ---- LIST SCREEN ---- */
.list-screen { padding: 0; }
.search-bar {
  padding: 12px 16px;
  background: white;
  border-bottom: 1px solid var(--slate100);
}
.search-input-wrap { position: relative; }
.search-input-wrap .s-icon {
  position: absolute; left: 12px; top: 50%; transform: translateY(-50%);
  color: var(--slate400); font-size: 16px; pointer-events: none;
}
.search-input-wrap input {
  width: 100%; padding: 11px 12px 11px 38px;
  border: 1.5px solid var(--slate200);
  border-radius: 12px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 14px; color: var(--slate900);
  background: var(--slate50);
  outline: none;
  transition: border-color 0.2s, background 0.2s;
}
.search-input-wrap input:focus {
  border-color: var(--g500); background: white;
}

.list-items { padding: 8px 0; }
.list-item {
  display: flex; align-items: center; gap: 12px;
  padding: 14px 16px;
  background: white;
  border-bottom: 1px solid var(--slate100);
  cursor: pointer;
  transition: background 0.12s;
  animation: slideIn 0.2s ease;
}
.list-item:hover { background: var(--g50); }
.list-item:last-child { border-bottom: none; }
.list-avatar {
  width: 44px; height: 44px; border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  font-size: 18px; font-weight: 700; flex-shrink: 0;
}
.list-info { flex: 1; min-width: 0; }
.list-name { font-size: 14px; font-weight: 700; color: var(--slate900); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.list-sub { font-size: 12px; color: var(--slate400); margin-top: 2px; }
.list-badge {
  font-size: 11px; font-weight: 700;
  padding: 3px 8px; border-radius: 20px;
  flex-shrink: 0;
}
.list-arrow { color: var(--slate300); font-size: 14px; }

.empty-state {
  display: flex; flex-direction: column; align-items: center;
  justify-content: center; padding: 64px 32px; gap: 12px;
  text-align: center;
}
.empty-icon { font-size: 56px; opacity: 0.4; }
.empty-title { font-size: 18px; font-weight: 700; color: var(--slate500); }
.empty-desc { font-size: 14px; color: var(--slate400); }

/* ---- FAB ---- */
.fab {
  position: fixed;
  bottom: calc(70px + env(safe-area-inset-bottom, 0px));
  right: calc(50% - 215px + 16px);
  width: 56px; height: 56px;
  background: linear-gradient(135deg, var(--g600), var(--g500));
  border-radius: 18px;
  border: none; cursor: pointer; color: white;
  font-size: 26px;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 6px 24px rgba(22,163,74,0.45);
  transition: all 0.18s;
  z-index: 100;
}
.fab:hover { transform: scale(1.05) translateY(-2px); }
.fab:active { transform: scale(0.96); }

/* ---- FORM ---- */
.form-screen { padding: 16px; }
.form-card {
  background: white;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 12px;
  box-shadow: var(--shadow);
}
.form-section-title {
  font-size: 13px; font-weight: 700;
  color: var(--g700); text-transform: uppercase;
  letter-spacing: 0.8px; margin-bottom: 16px;
  padding-bottom: 10px; border-bottom: 1.5px solid var(--g100);
  display: flex; align-items: center; gap: 6px;
}

.field { margin-bottom: 14px; }
.field:last-child { margin-bottom: 0; }
.field label {
  display: block; font-size: 12px; font-weight: 700;
  color: var(--slate600); margin-bottom: 5px;
  text-transform: uppercase; letter-spacing: 0.4px;
}
.field input, .field select, .field textarea {
  width: 100%; padding: 11px 12px;
  border: 1.5px solid var(--slate200);
  border-radius: 10px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 14px; color: var(--slate900);
  background: var(--slate50); outline: none;
  transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;
}
.field input:focus, .field select:focus, .field textarea:focus {
  border-color: var(--g500); background: white;
  box-shadow: 0 0 0 3px rgba(34,197,94,0.1);
}
.field textarea { resize: vertical; min-height: 72px; }
.field select { appearance: none; cursor: pointer; }
.field-row { display: flex; gap: 10px; }
.field-row .field { flex: 1; }

/* ---- PRODUCT SELECTOR (NF) ---- */
.product-picker {
  background: var(--slate50);
  border-radius: 10px;
  border: 1.5px solid var(--slate200);
  overflow: hidden;
}
.product-picker-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 12px;
  background: white;
  border-bottom: 1px solid var(--slate100);
}
.product-picker-title { font-size: 13px; font-weight: 700; color: var(--slate700); }
.product-picker-add {
  background: var(--g100); color: var(--g700);
  border: none; border-radius: 8px;
  padding: 5px 10px; font-size: 12px; font-weight: 700;
  cursor: pointer; font-family: 'Plus Jakarta Sans', sans-serif;
  display: flex; align-items: center; gap: 4px;
  transition: background 0.15s;
}
.product-picker-add:hover { background: var(--g200); }
.product-item {
  display: flex; align-items: center; gap: 10px;
  padding: 10px 12px;
  border-bottom: 1px solid var(--slate100);
}
.product-item:last-child { border-bottom: none; }
.product-item-name { flex: 1; font-size: 13px; font-weight: 600; color: var(--slate800); }
.product-qty-ctrl {
  display: flex; align-items: center; gap: 6px;
}
.qty-btn {
  width: 28px; height: 28px;
  border-radius: 8px; border: 1.5px solid var(--slate200);
  background: white; cursor: pointer;
  font-size: 16px; font-weight: 700; color: var(--slate700);
  display: flex; align-items: center; justify-content: center;
  transition: all 0.12s;
}
.qty-btn:hover { border-color: var(--g500); color: var(--g700); }
.qty-val { font-size: 14px; font-weight: 700; color: var(--slate900); min-width: 24px; text-align: center; }
.product-price { font-size: 13px; font-weight: 700; color: var(--g700); min-width: 60px; text-align: right; }
.product-remove { color: var(--red400); font-size: 18px; cursor: pointer; padding: 2px; transition: color 0.12s; }
.product-remove:hover { color: var(--red500); }

/* ---- PRODUCT PICKER MODAL ---- */
.picker-search-wrap {
  display: flex; align-items: center; gap: 8px;
  background: var(--slate50); border: 1.5px solid var(--slate200);
  border-radius: 10px; padding: 10px 12px; margin-bottom: 12px;
}
.picker-search-wrap input {
  flex: 1; border: none; background: transparent; outline: none;
  font-size: 14px; font-family: inherit; color: var(--slate900);
}
.picker-list {
  max-height: 280px; overflow-y: auto;
  border: 1.5px solid var(--slate200); border-radius: 10px;
  background: white;
}
.picker-empty {
  padding: 24px; text-align: center; color: var(--slate400); font-size: 13px;
}
.picker-item {
  display: flex; align-items: center; gap: 10px;
  padding: 10px 12px; cursor: pointer;
  border-bottom: 1px solid var(--slate100);
  transition: background 0.12s;
}
.picker-item:last-child { border-bottom: none; }
.picker-item:hover { background: var(--g50); }
.picker-item-icon {
  width: 36px; height: 36px; border-radius: 10px;
  background: var(--blue100); display: flex; align-items: center; justify-content: center;
  font-size: 18px;
}
.picker-item-info { flex: 1; min-width: 0; }
.picker-item-name { font-size: 13px; font-weight: 600; color: var(--slate800); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.picker-item-sub { font-size: 11px; color: var(--slate400); margin-top: 2px; }
.picker-item-price { font-size: 13px; font-weight: 700; color: var(--g700); white-space: nowrap; }
.picker-detail { padding: 4px 0; }
.picker-selected {
  display: flex; align-items: center; gap: 10px;
  background: var(--g50); border: 1.5px solid var(--g200);
  border-radius: 10px; padding: 12px;
}
.picker-selected-icon { font-size: 28px; }
.picker-selected-name { font-size: 14px; font-weight: 700; color: var(--slate800); }
.picker-selected-sub { font-size: 12px; color: var(--slate400); margin-top: 2px; }
.picker-change-btn {
  margin-left: auto; background: white; border: 1.5px solid var(--slate200);
  border-radius: 8px; padding: 4px 10px; font-size: 12px; font-weight: 600;
  cursor: pointer; color: var(--slate600); font-family: inherit;
  transition: all 0.12s;
}
.picker-change-btn:hover { border-color: var(--g500); color: var(--g700); }
.picker-total-row {
  display: flex; justify-content: space-between; align-items: center;
  margin-top: 16px; padding: 12px; background: var(--g50);
  border-radius: 10px; border: 1.5px solid var(--g200);
}
.picker-total-row span:first-child { font-size: 13px; font-weight: 600; color: var(--slate600); }
.picker-total-value { font-size: 18px; font-weight: 800; color: var(--g700); }

/* ---- NF STATUS ---- */
.nf-status {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 11px; font-weight: 700; padding: 3px 8px; border-radius: 20px;
}
.nf-status.AUTORIZADA { background: var(--g100); color: var(--g700); }
.nf-status.PENDENTE { background: var(--amber100); color: #92400e; }
.nf-status.REJEITADA { background: var(--red100); color: var(--red500); }
.nf-status.CANCELADA { background: var(--slate100); color: var(--slate500); }
.nf-status.RASCUNHO { background: var(--blue100); color: var(--blue500); }

/* ---- BUTTONS ---- */
.btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  padding: 13px 20px;
  border-radius: 12px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 15px; font-weight: 700;
  cursor: pointer; border: none; outline: none;
  transition: all 0.18s;
  width: 100%;
}
.btn-primary {
  background: linear-gradient(135deg, var(--g700), var(--g500));
  color: white;
  box-shadow: 0 4px 16px rgba(22,163,74,0.35);
}
.btn-primary:hover { transform: translateY(-1px); box-shadow: 0 6px 24px rgba(22,163,74,0.45); }
.btn-primary:active { transform: scale(0.98); }
.btn-primary:disabled { opacity: 0.55; cursor: not-allowed; transform: none; }
.btn-secondary { background: var(--g50); color: var(--g700); border: 1.5px solid var(--g200); }
.btn-secondary:hover { background: var(--g100); }
.btn-danger { background: var(--red100); color: var(--red500); border: 1.5px solid #fca5a5; }
.btn-danger:hover { background: #fecaca; }
.btn-ghost { background: transparent; color: var(--slate600); border: 1.5px solid var(--slate200); }
.btn-ghost:hover { background: var(--slate100); }
.btn-sm { padding: 8px 14px; font-size: 13px; border-radius: 10px; width: auto; }

/* ---- TOAST ---- */
.toast {
  position: fixed;
  bottom: calc(80px + env(safe-area-inset-bottom, 0px));
  left: 50%; transform: translateX(-50%);
  background: var(--slate900); color: white;
  padding: 12px 20px;
  border-radius: 12px;
  font-size: 14px; font-weight: 600;
  box-shadow: 0 8px 32px rgba(0,0,0,0.25);
  white-space: nowrap;
  z-index: 9999;
  animation: toastIn 0.3s ease;
  display: flex; align-items: center; gap: 8px;
  max-width: 380px;
}
.toast.success { background: var(--g800); }
.toast.error { background: #7f1d1d; }
.toast.info { background: #1e3a5f; }

/* ---- MODAL ---- */
.modal-overlay {
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.55);
  backdrop-filter: blur(4px);
  z-index: 500;
  display: flex; align-items: flex-end;
  animation: fadeIn 0.2s ease;
}
.modal-sheet {
  background: white;
  border-radius: 24px 24px 0 0;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  animation: slideUp 0.3s ease;
  padding-bottom: env(safe-area-inset-bottom, 16px);
}
.modal-sheet::-webkit-scrollbar { display: none; }
.modal-handle {
  width: 40px; height: 4px; background: var(--slate200);
  border-radius: 2px; margin: 12px auto 20px;
}
.modal-title {
  font-size: 18px; font-weight: 800; color: var(--slate900);
  padding: 0 20px 16px; border-bottom: 1px solid var(--slate100);
  margin-bottom: 4px;
}
.modal-content { padding: 12px 20px 20px; }
.modal-actions { display: flex; flex-direction: column; gap: 8px; padding: 0 20px 12px; }

/* ---- DETAIL SCREEN ---- */
.detail-hero {
  background: linear-gradient(135deg, var(--g800), var(--g600));
  padding: 52px 20px 28px;
  color: white;
}
.detail-hero-icon { font-size: 40px; margin-bottom: 8px; }
.detail-hero-name { font-size: 22px; font-weight: 800; letter-spacing: -0.3px; }
.detail-hero-sub { font-size: 13px; color: rgba(255,255,255,0.65); margin-top: 4px; }
.detail-body { padding: 16px; }
.info-group { background: white; border-radius: 14px; padding: 16px; margin-bottom: 12px; box-shadow: var(--shadow); }
.info-row { display: flex; justify-content: space-between; align-items: flex-start; padding: 8px 0; border-bottom: 1px solid var(--slate100); }
.info-row:last-child { border-bottom: none; padding-bottom: 0; }
.info-label { font-size: 12px; font-weight: 600; color: var(--slate400); text-transform: uppercase; letter-spacing: 0.4px; }
.info-value { font-size: 14px; font-weight: 600; color: var(--slate900); text-align: right; max-width: 60%; }
.action-row { display: flex; gap: 8px; margin-bottom: 12px; }

/* ---- LOADING ---- */
.loading-wrap { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 48px; gap: 12px; }
.spinner {
  width: 36px; height: 36px;
  border: 3px solid var(--g200);
  border-top-color: var(--g600);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

/* ---- PROFILE ---- */
.profile-screen { padding: 0; }
.profile-hero {
  background: linear-gradient(135deg, var(--g800), var(--g600));
  padding: 52px 20px 32px;
  display: flex; flex-direction: column; align-items: center;
  color: white;
}
.profile-avatar {
  width: 80px; height: 80px;
  background: rgba(255,255,255,0.2);
  border-radius: 24px;
  display: flex; align-items: center; justify-content: center;
  font-size: 36px; margin-bottom: 12px;
  border: 2px solid rgba(255,255,255,0.3);
}
.profile-name { font-size: 20px; font-weight: 800; }
.profile-role { font-size: 13px; color: rgba(255,255,255,0.65); margin-top: 2px; }
.profile-body { padding: 16px; }
.menu-list { background: white; border-radius: 14px; overflow: hidden; box-shadow: var(--shadow); margin-bottom: 12px; }
.menu-list-item {
  display: flex; align-items: center; gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid var(--slate100);
  cursor: pointer; transition: background 0.12s;
}
.menu-list-item:last-child { border-bottom: none; }
.menu-list-item:hover { background: var(--g50); }
.menu-list-icon { width: 36px; height: 36px; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 18px; flex-shrink: 0; }
.menu-list-label { flex: 1; font-size: 15px; font-weight: 600; color: var(--slate800); }
.menu-list-arrow { color: var(--slate300); font-size: 14px; }

/* ---- PEDIDO ---- */
.pedido-status {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 11px; font-weight: 700; padding: 3px 8px; border-radius: 20px;
}
.pedido-status.ABERTO { background: var(--blue100); color: var(--blue500); }
.pedido-status.FATURADO { background: var(--g100); color: var(--g700); }
.pedido-status.CANCELADO { background: var(--slate100); color: var(--slate500); }

/* ---- UTILS ---- */
:root { --shadow: 0 2px 10px rgba(0,0,0,0.06); --r: 14px; }
.mt8 { margin-top: 8px; }
.mt12 { margin-top: 12px; }
.mt16 { margin-top: 16px; }
.gap8 { gap: 8px; }
.flex { display: flex; }
.flex-col { flex-direction: column; }
.items-center { align-items: center; }
.justify-between { justify-content: space-between; }
.text-green { color: var(--g700); }
.text-red { color: var(--red500); }
.text-sm { font-size: 13px; }
.font-bold { font-weight: 700; }

@keyframes spin { to { transform: rotate(360deg); } }
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
@keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
@keyframes slideIn { from { opacity: 0; transform: translateX(-10px); } to { opacity: 1; transform: translateX(0); } }
@keyframes toastIn { from { opacity: 0; transform: translateX(-50%) translateY(20px); } to { opacity: 1; transform: translateX(-50%) translateY(0); } }
`;

// ============================================================
// CONTROLLER HOOKS
// ============================================================
function useToast() {
  const [toast, setToast] = useState(null);
  const show = useCallback((msg, type = "info") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3200);
  }, []);
  return { toast, show };
}

function useList(fetchFn) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { const data = await fetchFn(); setItems(Array.isArray(data) ? data : []); }
    catch (e) { setError(e.message); setItems([]); }
    finally { setLoading(false); }
  }, [fetchFn]);

  useEffect(() => { load(); }, [load]);
  return { items, setItems, loading, error, reload: load };
}

// ============================================================
// VIEW COMPONENTS
// ============================================================

// Toast
function Toast({ toast }) {
  if (!toast) return null;
  const icons = { success: "✅", error: "❌", info: "ℹ️" };
  return <div className={`toast ${toast.type}`}>{icons[toast.type] || "💬"} {toast.msg}</div>;
}

// Loading
function Loading() {
  return <div className="loading-wrap"><div className="spinner" /><span style={{ color: "var(--slate400)", fontSize: 14 }}>Carregando...</span></div>;
}

// Empty
function Empty({ icon = "📭", title = "Nenhum item", desc = "Toque em + para adicionar" }) {
  return (
    <div className="empty-state">
      <div className="empty-icon">{icon}</div>
      <div className="empty-title">{title}</div>
      <div className="empty-desc">{desc}</div>
    </div>
  );
}

// Modal Sheet
function Modal({ open, onClose, title, children, actions }) {
  if (!open) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-sheet" onClick={e => e.stopPropagation()}>
        <div className="modal-handle" />
        {title && <div className="modal-title">{title}</div>}
        <div className="modal-content">{children}</div>
        {actions && <div className="modal-actions">{actions}</div>}
      </div>
    </div>
  );
}

// Field component
function Field({ label, children }) {
  return <div className="field"><label>{label}</label>{children}</div>;
}

// Format currency
const fmt = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("pt-BR") : "--";

// ProductPickerModal — pesquisa produto, define quantidade e preço
function ProductPickerModal({ open, onClose, produtos, onConfirm }) {
  const [search, setSearch] = useState("");
  const [selectedProd, setSelectedProd] = useState(null);
  const [quantidade, setQuantidade] = useState(1);
  const [valorUnitario, setValorUnitario] = useState("");

  useEffect(() => {
    if (open) { setSearch(""); setSelectedProd(null); setQuantidade(1); setValorUnitario(""); }
  }, [open]);

  if (!open) return null;

  const filtered = produtos.filter(p =>
    (p.descricao || "").toLowerCase().includes(search.toLowerCase()) ||
    (p.referencia || "").toLowerCase().includes(search.toLowerCase()) ||
    (p.gtin || "").includes(search)
  );

  const handleSelect = (p) => {
    setSelectedProd(p);
    setValorUnitario(Number(p.preco_venda || 0).toFixed(2));
    setQuantidade(1);
  };

  const handleConfirm = () => {
    if (!selectedProd) return;
    const qty = Math.max(1, Number(quantidade) || 1);
    const price = Math.max(0, Number(valorUnitario) || 0);
    onConfirm({
      produto_id: selectedProd.id,
      descricao: selectedProd.descricao,
      quantidade: qty,
      valor_unitario: price,
      valor_total: qty * price,
    });
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-sheet" onClick={e => e.stopPropagation()}>
        <div className="modal-handle" />
        <div className="modal-title">📦 Adicionar Produto</div>
        <div className="modal-content">
          {!selectedProd ? (
            <>
              <div className="picker-search-wrap">
                <span className="s-icon">🔍</span>
                <input
                  autoFocus
                  placeholder="Buscar por nome, referência ou código..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
              <div className="picker-list">
                {filtered.length === 0 ? (
                  <div className="picker-empty">Nenhum produto encontrado</div>
                ) : filtered.map(p => (
                  <div key={p.id} className="picker-item" onClick={() => handleSelect(p)}>
                    <div className="picker-item-icon">📦</div>
                    <div className="picker-item-info">
                      <div className="picker-item-name">{p.descricao}</div>
                      <div className="picker-item-sub">
                        {p.referencia ? `Ref: ${p.referencia}` : ""}{p.referencia && p.gtin ? " • " : ""}{p.gtin ? `GTIN: ${p.gtin}` : ""}
                        {!p.referencia && !p.gtin ? "Sem referência" : ""}
                      </div>
                    </div>
                    <div className="picker-item-price">{fmt(p.preco_venda)}</div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="picker-detail">
              <div className="picker-selected">
                <div className="picker-selected-icon">📦</div>
                <div>
                  <div className="picker-selected-name">{selectedProd.descricao}</div>
                  <div className="picker-selected-sub">{selectedProd.referencia || "Sem referência"} • {fmt(selectedProd.preco_venda)}</div>
                </div>
                <button className="picker-change-btn" onClick={() => setSelectedProd(null)}>Trocar</button>
              </div>
              <div className="field-row" style={{ marginTop: 16 }}>
                <Field label="Quantidade">
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={quantidade}
                    onChange={e => setQuantidade(e.target.value)}
                  />
                </Field>
                <Field label="Preço Unitário (R$)">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={valorUnitario}
                    onChange={e => setValorUnitario(e.target.value)}
                  />
                </Field>
              </div>
              <div className="picker-total-row">
                <span>Total do item:</span>
                <span className="picker-total-value">{fmt((Number(quantidade) || 0) * (Number(valorUnitario) || 0))}</span>
              </div>
            </div>
          )}
        </div>
        {selectedProd && (
          <div className="modal-actions">
            <button className="btn btn-primary" onClick={handleConfirm}>✅ Confirmar</button>
            <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// SCREENS
// ============================================================

// ---------- LOGIN ----------
function LoginScreen({ onLogin }) {
  const [form, setForm] = useState({ username: "cgcom", password: "#Dresp3w@" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = async (e) => {
    e?.preventDefault();
    setLoading(true); setError("");
    try {
      await ApiModel.login(form.username, form.password);
      onLogin(form.username);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="login-screen">
      <div className="login-circles">
        <span /><span /><span />
      </div>
      <div className="login-hero">
        <div className="login-icon-wrap">🌱</div>
        <div className="login-app-name">Agronotas</div>
        <div className="login-tagline">Gestão de Notas Fiscais</div>
      </div>
      <div className="login-card">
        <h2>Bem-vindo!</h2>
        <p>Faça login para continuar</p>
        <Field label="Usuário">
          <input
            type="text"
            value={form.username}
            placeholder="Digite seu usuário"
            onChange={e => setForm({ ...form, username: e.target.value })}
          />
        </Field>
        <Field label="Senha">
          <input
            type="password"
            value={form.password}
            placeholder="Digite sua senha"
            onChange={e => setForm({ ...form, password: e.target.value })}
          />
        </Field>
        {error && (
          <div style={{ background: "var(--red100)", color: "var(--red500)", borderRadius: 10, padding: "10px 14px", fontSize: 14, fontWeight: 600, marginBottom: 12 }}>
            ⚠️ {error}
          </div>
        )}
        <button className="btn btn-primary" onClick={handleLogin} disabled={loading}>
          {loading ? <><div style={{ width: 18, height: 18, border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "white", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} /> Entrando...</> : "🔐 Entrar"}
        </button>
        <div style={{ textAlign: "center", marginTop: 16, fontSize: 12, color: "var(--slate400)" }}>
          Ambiente de Homologação — CGCOM
        </div>
      </div>
    </div>
  );
}

// ---------- DASHBOARD ----------
function DashboardScreen({ onNavigate }) {
  const menus = [
    { key: "clientes", icon: "👥", color: "#dcfce7", iconBg: "#16a34a", name: "Clientes", desc: "Gerenciar pessoas" },
    { key: "produtos", icon: "📦", color: "#dbeafe", iconBg: "#3b82f6", name: "Produtos", desc: "Estoque e preços" },
    { key: "pedidos", icon: "🛒", color: "#fef3c7", iconBg: "#d97706", name: "Pedidos", desc: "Controle de pedidos" },
    { key: "nfe", icon: "🧾", color: "#f3e8ff", iconBg: "#9333ea", name: "Notas Fiscais", desc: "Emitir NF-e" },
    { key: "formas_pgto", icon: "💳", color: "#ffe4e6", iconBg: "#e11d48", name: "Formas Pgto", desc: "Pagamentos" },
    { key: "operacoes", icon: "⚙️", color: "#f0fdf4", iconBg: "#15803d", name: "Operações", desc: "Operações fiscais" },
  ];

  const now = new Date();
  const dtStr = now.toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "long" });

  return (
    <div className="content">
      <div className="dashboard-header">
        <div style={{ position: "relative", zIndex: 1 }}>
          <div className="dash-greeting">Olá, bem-vindo ao</div>
          <div className="dash-title">🌱 Agronotas</div>
          <div className="dash-date">{dtStr}</div>
        </div>
      </div>
      <div className="dash-stats-row">
        {[
          { icon: "👥", val: "—", lbl: "Clientes" },
          { icon: "📦", val: "—", lbl: "Produtos" },
          { icon: "🧾", val: "—", lbl: "NF-e" },
        ].map(s => (
          <div key={s.lbl} className="stat-card">
            <div className="stat-icon">{s.icon}</div>
            <div className="stat-val">{s.val}</div>
            <div className="stat-lbl">{s.lbl}</div>
          </div>
        ))}
      </div>

      <div className="section-header" style={{ marginTop: 8 }}>
        <div className="section-title">Menu Principal</div>
      </div>

      <div className="menu-grid">
        {menus.map(m => (
          <button key={m.key} className="menu-card" onClick={() => onNavigate(m.key)}>
            <div className="menu-card-icon" style={{ background: m.color }}>
              {m.icon}
            </div>
            <div>
              <div className="menu-card-name">{m.name}</div>
              <div className="menu-card-desc">{m.desc}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------- CLIENTES ----------
function ClientesScreen({ toast }) {
  const fetchFn = useCallback(() => ApiModel.pessoas.listar(), []);
  const { items, loading, reload } = useList(fetchFn);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);

  const filtered = items.filter(p =>
    (p.nome || "").toLowerCase().includes(search.toLowerCase()) ||
    (p.cnpj_cpf || "").includes(search)
  );

  const openNew = () => {
    setFormData({ tipo_inscricao: "J", situacao: "A", situacao_cliente: "A", situacao_transportadora: "I", situacao_fornecedor: "I", situacao_associado: "I", empresa_id: EMPRESA_ID });
    setShowForm(true);
  };

  const openEdit = (p) => {
    setFormData({ ...p });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formData.nome) return toast.show("Nome é obrigatório", "error");
    setSaving(true);
    try {
      if (formData.id) await ApiModel.pessoas.alterar(formData);
      else await ApiModel.pessoas.cadastrar(formData);
      toast.show(formData.id ? "Cliente atualizado!" : "Cliente cadastrado!", "success");
      setShowForm(false); reload();
    } catch (e) { toast.show(e.message, "error"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await ApiModel.pessoas.excluir(id);
      toast.show("Cliente excluído", "success");
      setSelected(null); reload();
    } catch (e) { toast.show(e.message, "error"); }
  };

  if (selected) {
    return (
      <div className="content">
        <div className="detail-hero">
          <div className="detail-hero-icon">👤</div>
          <div className="detail-hero-name">{selected.nome}</div>
          <div className="detail-hero-sub">{selected.cnpj_cpf} • {selected.tipo_inscricao === "J" ? "PJ" : "PF"}</div>
        </div>
        <div className="detail-body">
          <div className="action-row">
            <button className="btn btn-secondary btn-sm" style={{ flex: 1 }} onClick={() => openEdit(selected)}>✏️ Editar</button>
            <button className="btn btn-danger btn-sm" style={{ flex: 1 }} onClick={() => handleDelete(selected.id)}>🗑️ Excluir</button>
          </div>
          <div className="info-group">
            {[
              ["Nome", selected.nome],
              ["Apelido", selected.apelido || "—"],
              ["CPF/CNPJ", selected.cnpj_cpf || "—"],
              ["IE/RG", selected.ies_rg || "—"],
              ["Tipo", selected.tipo_contribuinte || "—"],
              ["Situação", selected.situacao === "A" ? "Ativo" : "Inativo"],
            ].map(([l, v]) => (
              <div key={l} className="info-row">
                <span className="info-label">{l}</span>
                <span className="info-value">{v}</span>
              </div>
            ))}
          </div>
          <div className="info-group">
            <div className="form-section-title">📍 Endereço</div>
            {[
              ["Logradouro", selected.logradouro || "—"],
              ["Número", selected.numero || "—"],
              ["Bairro", selected.bairro || "—"],
              ["CEP", selected.cep || "—"],
              ["Complemento", selected.complemento || "—"],
            ].map(([l, v]) => (
              <div key={l} className="info-row">
                <span className="info-label">{l}</span>
                <span className="info-value">{v}</span>
              </div>
            ))}
          </div>
          <button className="btn btn-ghost mt12" onClick={() => setSelected(null)}>← Voltar</button>
        </div>
      </div>
    );
  }

  return (
    <div className="content list-screen">
      <div className="search-bar">
        <div className="search-input-wrap">
          <span className="s-icon">🔍</span>
          <input placeholder="Buscar por nome ou CPF/CNPJ..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>
      {loading ? <Loading /> : filtered.length === 0 ? <Empty icon="👥" title="Nenhum cliente" desc="Toque em + para cadastrar" /> : (
        <div className="list-items">
          {filtered.map(p => (
            <div key={p.id} className="list-item" onClick={() => setSelected(p)}>
              <div className="list-avatar" style={{ background: "var(--g100)", color: "var(--g700)" }}>
                {(p.nome || "?")[0].toUpperCase()}
              </div>
              <div className="list-info">
                <div className="list-name">{p.nome}</div>
                <div className="list-sub">{p.cnpj_cpf || "Sem documento"} • {p.tipo_inscricao === "J" ? "PJ" : "PF"}</div>
              </div>
              <span className={`list-badge ${p.situacao === "A" ? "nf-status AUTORIZADA" : "nf-status CANCELADA"}`}>
                {p.situacao === "A" ? "Ativo" : "Inativo"}
              </span>
              <span className="list-arrow">›</span>
            </div>
          ))}
        </div>
      )}
      <button className="fab" onClick={openNew} title="Novo cliente">+</button>
      <Modal open={showForm} onClose={() => setShowForm(false)} title={formData.id ? "✏️ Editar Cliente" : "➕ Novo Cliente"}
        actions={[
          <button key="s" className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? "Salvando..." : "💾 Salvar"}</button>,
          <button key="c" className="btn btn-ghost" onClick={() => setShowForm(false)}>Cancelar</button>
        ]}>
        <Field label="Nome *"><input value={formData.nome || ""} placeholder="Nome completo" onChange={e => setFormData({ ...formData, nome: e.target.value })} /></Field>
        <Field label="Apelido"><input value={formData.apelido || ""} placeholder="Apelido ou fantasia" onChange={e => setFormData({ ...formData, apelido: e.target.value })} /></Field>
        <div className="field-row">
          <Field label="CPF/CNPJ"><input value={formData.cnpj_cpf || ""} placeholder="00.000.000/0001-00" onChange={e => setFormData({ ...formData, cnpj_cpf: e.target.value })} /></Field>
          <Field label="Tipo">
            <select value={formData.tipo_inscricao || "J"} onChange={e => setFormData({ ...formData, tipo_inscricao: e.target.value })}>
              <option value="J">Jurídica</option>
              <option value="F">Física</option>
            </select>
          </Field>
        </div>
        <Field label="IE/RG"><input value={formData.ies_rg || ""} placeholder="Inscrição Estadual" onChange={e => setFormData({ ...formData, ies_rg: e.target.value })} /></Field>
        <Field label="Tipo Contribuinte">
          <select value={formData.tipo_contribuinte || ""} onChange={e => setFormData({ ...formData, tipo_contribuinte: e.target.value })}>
            <option value="">Selecione</option>
            <option value="CONTRIBUINTE">Contribuinte</option>
            <option value="NAO_CONTRIBUINTE">Não Contribuinte</option>
            <option value="ISENTO">Isento</option>
          </select>
        </Field>
        <Field label="Logradouro"><input value={formData.logradouro || ""} placeholder="Rua, Av..." onChange={e => setFormData({ ...formData, logradouro: e.target.value })} /></Field>
        <div className="field-row">
          <Field label="Número"><input value={formData.numero || ""} placeholder="123" onChange={e => setFormData({ ...formData, numero: e.target.value })} /></Field>
          <Field label="CEP"><input value={formData.cep || ""} placeholder="00000-000" onChange={e => setFormData({ ...formData, cep: e.target.value })} /></Field>
        </div>
        <div className="field-row">
          <Field label="Bairro"><input value={formData.bairro || ""} placeholder="Bairro" onChange={e => setFormData({ ...formData, bairro: e.target.value })} /></Field>
          <Field label="Situação">
            <select value={formData.situacao || "A"} onChange={e => setFormData({ ...formData, situacao: e.target.value })}>
              <option value="A">Ativo</option>
              <option value="I">Inativo</option>
            </select>
          </Field>
        </div>
        <Field label="E-mail"><input type="email" value={formData.email || ""} placeholder="email@empresa.com" onChange={e => setFormData({ ...formData, email: e.target.value })} /></Field>
        <div className="field-row">
          <Field label="Telefone"><input value={formData.fone || ""} placeholder="(00) 0000-0000" onChange={e => setFormData({ ...formData, fone: e.target.value })} /></Field>
          <Field label="Celular"><input value={formData.celular || ""} placeholder="(00) 00000-0000" onChange={e => setFormData({ ...formData, celular: e.target.value })} /></Field>
        </div>
      </Modal>
    </div>
  );
}

// ---------- PRODUTOS ----------
function ProdutosScreen({ toast }) {
  const fetchFn = useCallback(() => ApiModel.produtos.listar(), []);
  const { items, loading, reload } = useList(fetchFn);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [ncms, setNcms] = useState([]);
  const [unidades, setUnidades] = useState([]);

  useEffect(() => {
    ApiModel.ncms.listar().then(setNcms).catch(() => {});
    ApiModel.unidades.listar().then(setUnidades).catch(() => {});
  }, []);

  const filtered = items.filter(p =>
    (p.descricao || "").toLowerCase().includes(search.toLowerCase()) ||
    (p.referencia || "").toLowerCase().includes(search.toLowerCase())
  );

  const openNew = () => {
    setFormData({ situacao: "A", empresa_id: EMPRESA_ID, peso_liquido: 0, peso_bruto: 0 });
    setShowForm(true);
  };

  const openEdit = (p) => { setFormData({ ...p }); setShowForm(true); };

  const handleSave = async () => {
    if (!formData.descricao) return toast.show("Descrição é obrigatória", "error");
    setSaving(true);
    try {
      if (formData.id) await ApiModel.produtos.alterar(formData);
      else await ApiModel.produtos.cadastrar(formData);
      toast.show(formData.id ? "Produto atualizado!" : "Produto cadastrado!", "success");
      setShowForm(false); reload();
    } catch (e) { toast.show(e.message, "error"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await ApiModel.produtos.excluir(id);
      toast.show("Produto excluído", "success");
      setSelected(null); reload();
    } catch (e) { toast.show(e.message, "error"); }
  };

  if (selected) {
    return (
      <div className="content">
        <div className="detail-hero">
          <div className="detail-hero-icon">📦</div>
          <div className="detail-hero-name">{selected.descricao}</div>
          <div className="detail-hero-sub">Ref: {selected.referencia || "—"} • GTIN: {selected.gtin || "—"}</div>
        </div>
        <div className="detail-body">
          <div className="action-row">
            <button className="btn btn-secondary btn-sm" style={{ flex: 1 }} onClick={() => openEdit(selected)}>✏️ Editar</button>
            <button className="btn btn-danger btn-sm" style={{ flex: 1 }} onClick={() => handleDelete(selected.id)}>🗑️ Excluir</button>
          </div>
          <div className="info-group">
            {[
              ["Descrição", selected.descricao],
              ["Referência", selected.referencia || "—"],
              ["GTIN", selected.gtin || "—"],
              ["Venda", fmt(selected.preco_venda)],
              ["Compra", fmt(selected.preco_compra)],
              ["Custo", fmt(selected.preco_custo)],
              ["Situação", selected.situacao === "A" ? "Ativo" : "Inativo"],
            ].map(([l, v]) => (
              <div key={l} className="info-row">
                <span className="info-label">{l}</span>
                <span className="info-value">{v}</span>
              </div>
            ))}
          </div>
          <button className="btn btn-ghost mt12" onClick={() => setSelected(null)}>← Voltar</button>
        </div>
      </div>
    );
  }

  return (
    <div className="content list-screen">
      <div className="search-bar">
        <div className="search-input-wrap">
          <span className="s-icon">🔍</span>
          <input placeholder="Buscar por descrição ou referência..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>
      {loading ? <Loading /> : filtered.length === 0 ? <Empty icon="📦" title="Nenhum produto" desc="Toque em + para cadastrar" /> : (
        <div className="list-items">
          {filtered.map(p => (
            <div key={p.id} className="list-item" onClick={() => setSelected(p)}>
              <div className="list-avatar" style={{ background: "var(--blue100)", color: "var(--blue500)" }}>📦</div>
              <div className="list-info">
                <div className="list-name">{p.descricao}</div>
                <div className="list-sub">Ref: {p.referencia || "—"} • {fmt(p.preco_venda)}</div>
              </div>
              <span className={`list-badge ${p.situacao === "A" ? "nf-status AUTORIZADA" : "nf-status CANCELADA"}`}>
                {p.situacao === "A" ? "Ativo" : "Inativo"}
              </span>
              <span className="list-arrow">›</span>
            </div>
          ))}
        </div>
      )}
      <button className="fab" onClick={openNew}>+</button>
      <Modal open={showForm} onClose={() => setShowForm(false)} title={formData.id ? "✏️ Editar Produto" : "➕ Novo Produto"}
        actions={[
          <button key="s" className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? "Salvando..." : "💾 Salvar"}</button>,
          <button key="c" className="btn btn-ghost" onClick={() => setShowForm(false)}>Cancelar</button>
        ]}>
        <Field label="Descrição *"><input value={formData.descricao || ""} placeholder="Nome do produto" onChange={e => setFormData({ ...formData, descricao: e.target.value })} /></Field>
        <Field label="Descrição Detalhada"><textarea value={formData.descricao_detalhada || ""} placeholder="Detalhes..." onChange={e => setFormData({ ...formData, descricao_detalhada: e.target.value })} /></Field>
        <div className="field-row">
          <Field label="GTIN"><input value={formData.gtin || ""} placeholder="Código de barras" onChange={e => setFormData({ ...formData, gtin: e.target.value })} /></Field>
          <Field label="Referência"><input value={formData.referencia || ""} placeholder="REF001" onChange={e => setFormData({ ...formData, referencia: e.target.value })} /></Field>
        </div>
        <div className="field-row">
          <Field label="Preço Venda"><input type="number" step="0.01" value={formData.preco_venda || ""} placeholder="0,00" onChange={e => setFormData({ ...formData, preco_venda: e.target.value })} /></Field>
          <Field label="Preço Compra"><input type="number" step="0.01" value={formData.preco_compra || ""} placeholder="0,00" onChange={e => setFormData({ ...formData, preco_compra: e.target.value })} /></Field>
        </div>
        <div className="field-row">
          <Field label="NCM">
            <select value={formData.ncm_id || ""} onChange={e => setFormData({ ...formData, ncm_id: e.target.value })}>
              <option value="">Selecione</option>
              {ncms.map(n => <option key={n.id} value={n.id}>{n.codigo} - {(n.descricao || "").slice(0, 25)}</option>)}
            </select>
          </Field>
          <Field label="Unidade">
            <select value={formData.unidade_id || ""} onChange={e => setFormData({ ...formData, unidade_id: e.target.value })}>
              <option value="">Selecione</option>
              {unidades.map(u => <option key={u.id} value={u.id}>{u.sigla} - {u.descricao}</option>)}
            </select>
          </Field>
        </div>
        <div className="field-row">
          <Field label="Peso Liq."><input type="number" step="0.001" value={formData.peso_liquido || ""} placeholder="0,000" onChange={e => setFormData({ ...formData, peso_liquido: e.target.value })} /></Field>
          <Field label="Situação">
            <select value={formData.situacao || "A"} onChange={e => setFormData({ ...formData, situacao: e.target.value })}>
              <option value="A">Ativo</option>
              <option value="I">Inativo</option>
            </select>
          </Field>
        </div>
      </Modal>
    </div>
  );
}

// ---------- FORMAS DE PAGAMENTO ----------
function FormasPgtoScreen({ toast }) {
  const [items, setItems] = useState(FormasPgtoModel.listar());
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({});
  const reload = () => setItems(FormasPgtoModel.listar());

  const handleSave = () => {
    if (!formData.descricao) return toast.show("Descrição obrigatória", "error");
    FormasPgtoModel.salvar({ ...formData });
    toast.show("Salvo com sucesso!", "success");
    setShowForm(false); reload();
  };

  const handleDelete = (id) => {
    FormasPgtoModel.excluir(id);
    toast.show("Excluído", "success"); reload();
  };

  return (
    <div className="content list-screen">
      {items.length === 0 ? <Empty icon="💳" title="Nenhuma forma de pagamento" /> : (
        <div className="list-items">
          {items.map(p => (
            <div key={p.id} className="list-item">
              <div className="list-avatar" style={{ background: "var(--red100)", color: "var(--red500)" }}>💳</div>
              <div className="list-info">
                <div className="list-name">{p.descricao}</div>
                <div className="list-sub">ID: {p.id}</div>
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <button className="btn btn-secondary btn-sm" onClick={() => { setFormData({ ...p }); setShowForm(true); }}>✏️</button>
                <button className="btn btn-danger btn-sm" onClick={() => handleDelete(p.id)}>🗑️</button>
              </div>
            </div>
          ))}
        </div>
      )}
      <button className="fab" onClick={() => { setFormData({}); setShowForm(true); }}>+</button>
      <Modal open={showForm} onClose={() => setShowForm(false)} title={formData.id ? "✏️ Editar" : "➕ Nova Forma de Pagamento"}
        actions={[
          <button key="s" className="btn btn-primary" onClick={handleSave}>💾 Salvar</button>,
          <button key="c" className="btn btn-ghost" onClick={() => setShowForm(false)}>Cancelar</button>
        ]}>
        <Field label="Descrição *"><input value={formData.descricao || ""} placeholder="Ex: DINHEIRO, PIX..." onChange={e => setFormData({ ...formData, descricao: e.target.value })} /></Field>
      </Modal>
    </div>
  );
}

// ---------- OPERAÇÕES FISCAIS ----------
function OperacoesScreen({ toast }) {
  const [items, setItems] = useState(OperacoesModel.listar());
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({});
  const reload = () => setItems(OperacoesModel.listar());

  const handleSave = () => {
    if (!formData.descricao) return toast.show("Descrição obrigatória", "error");
    OperacoesModel.salvar({ ...formData });
    toast.show("Salvo!", "success");
    setShowForm(false); reload();
  };

  const handleDelete = (id) => {
    OperacoesModel.excluir(id);
    toast.show("Excluído", "success"); reload();
  };

  return (
    <div className="content list-screen">
      {items.length === 0 ? <Empty icon="⚙️" title="Nenhuma operação" /> : (
        <div className="list-items">
          {items.map(p => (
            <div key={p.id} className="list-item">
              <div className="list-avatar" style={{ background: "var(--g100)", color: "var(--g700)" }}>⚙️</div>
              <div className="list-info">
                <div className="list-name">{p.descricao}</div>
                <div className="list-sub">ID: {p.id}</div>
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <button className="btn btn-secondary btn-sm" onClick={() => { setFormData({ ...p }); setShowForm(true); }}>✏️</button>
                <button className="btn btn-danger btn-sm" onClick={() => handleDelete(p.id)}>🗑️</button>
              </div>
            </div>
          ))}
        </div>
      )}
      <button className="fab" onClick={() => { setFormData({}); setShowForm(true); }}>+</button>
      <Modal open={showForm} onClose={() => setShowForm(false)} title={formData.id ? "✏️ Editar" : "➕ Nova Operação"}
        actions={[
          <button key="s" className="btn btn-primary" onClick={handleSave}>💾 Salvar</button>,
          <button key="c" className="btn btn-ghost" onClick={() => setShowForm(false)}>Cancelar</button>
        ]}>
        <Field label="Descrição *"><input value={formData.descricao || ""} placeholder="Ex: VENDA DE MERCADORIA" onChange={e => setFormData({ ...formData, descricao: e.target.value })} /></Field>
        <Field label="CFOP"><input value={formData.cfop || ""} placeholder="Ex: 5102" onChange={e => setFormData({ ...formData, cfop: e.target.value })} /></Field>
        <Field label="Observação"><textarea value={formData.observacao || ""} placeholder="Observações..." onChange={e => setFormData({ ...formData, observacao: e.target.value })} /></Field>
      </Modal>
    </div>
  );
}

// ---------- PEDIDOS ----------
function PedidosScreen({ toast }) {
  const [items, setItems] = useState(PedidoModel.listar());
  const [clientes, setClientes] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [formasPgto] = useState(FormasPgtoModel.listar());
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({});
  const [itensPedido, setItensPedido] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showPicker, setShowPicker] = useState(false);

  const reload = () => setItems(PedidoModel.listar());

  useEffect(() => {
    ApiModel.pessoas.listar().then(setClientes).catch(() => {});
    ApiModel.produtos.listar().then(setProdutos).catch(() => {});
  }, []);

  const openNew = () => {
    setFormData({ status: "ABERTO", ambiente: "HOMOLOGACAO" });
    setItensPedido([]);
    setShowForm(true);
  };

  const addItem = (item) => {
    setItensPedido(prev => [...prev, item]);
  };

  const updateItem = (idx, field, value) => {
    setItensPedido(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const updated = { ...item, [field]: value };
      if (field === "produto_id") {
        const p = produtos.find(p => p.id === Number(value));
        if (p) { updated.descricao = p.descricao; updated.valor_unitario = Number(p.preco_venda || 0); }
      }
      updated.valor_total = Number(updated.quantidade || 0) * Number(updated.valor_unitario || 0);
      return updated;
    }));
  };

  const removeItem = (idx) => setItensPedido(prev => prev.filter((_, i) => i !== idx));

  const totalPedido = itensPedido.reduce((s, i) => s + Number(i.valor_total || 0), 0);

  const handleSave = () => {
    if (!formData.cliente_id) return toast.show("Selecione o cliente", "error");
    if (!itensPedido.length) return toast.show("Adicione pelo menos 1 produto", "error");
    const pedido = { ...formData, itens: itensPedido, valor_total: totalPedido };
    PedidoModel.salvar(pedido);
    toast.show(formData.id ? "Pedido atualizado!" : "Pedido criado!", "success");
    setShowForm(false); reload();
  };

  const handleDelete = (id) => {
    PedidoModel.excluir(id);
    toast.show("Pedido excluído", "success");
    setSelected(null); reload();
  };

  const getCliente = (id) => clientes.find(c => c.id === Number(id));
  const getFormaPgto = (id) => formasPgto.find(f => f.id === Number(id));

  const printPedido = (pedido) => {
    const cli = getCliente(pedido.cliente_id);
    const pgto = getFormaPgto(pedido.forma_pagamento_id);
    const itens = pedido.itens || [];
    const w = window.open("", "_blank", "width=820,height=600");
    if (!w) return;
    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Pedido #${pedido.id?.toString().slice(-4)}</title>
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family: 'Segoe UI', Arial, sans-serif; color:#1e293b; padding:30px 40px; font-size:13px; }
  .header { display:flex; align-items:center; gap:20px; border-bottom:3px solid #16a34a; padding-bottom:18px; margin-bottom:18px; }
  .logo { width:70px; height:70px; background:#16a34a; border-radius:16px; display:flex; align-items:center; justify-content:center; font-size:36px; color:white; flex-shrink:0; }
  .empresa-info { flex:1; }
  .empresa-nome { font-size:22px; font-weight:800; color:#16a34a; }
  .empresa-detalhe { font-size:12px; color:#475569; margin-top:2px; }
  .pedido-num { text-align:right; }
  .pedido-num h2 { font-size:18px; color:#16a34a; font-weight:800; }
  .pedido-num span { font-size:12px; color:#64748b; }
  .section { margin-bottom:16px; }
  .section-title { font-size:12px; font-weight:700; color:#16a34a; text-transform:uppercase; letter-spacing:.5px; margin-bottom:8px; border-bottom:1px solid #e2e8f0; padding-bottom:4px; }
  .cliente-grid { display:grid; grid-template-columns:1fr 1fr; gap:4px 24px; }
  .campo { font-size:12px; }
  .campo b { color:#334155; }
  table { width:100%; border-collapse:collapse; margin-top:6px; }
  th { background:#f1f5f9; color:#334155; font-size:11px; font-weight:700; text-transform:uppercase; padding:8px 10px; text-align:left; border-bottom:2px solid #e2e8f0; }
  td { padding:7px 10px; border-bottom:1px solid #f1f5f9; font-size:12px; }
  tr:nth-child(even) { background:#fafafa; }
  .text-right { text-align:right; }
  .total-row td { font-weight:800; font-size:14px; color:#16a34a; border-top:2px solid #16a34a; background:transparent; }
  .footer { display:grid; grid-template-columns:1fr 1fr; gap:24px; margin-top:18px; padding-top:14px; border-top:2px solid #e2e8f0; }
  .footer .section { margin-bottom:0; }
  .obs-text { background:#f8fafc; border:1px solid #e2e8f0; border-radius:6px; padding:8px 10px; font-size:12px; color:#475569; min-height:40px; white-space:pre-wrap; }
  .print-footer { text-align:center; color:#94a3b8; font-size:10px; margin-top:30px; padding-top:12px; border-top:1px solid #f1f5f9; }
  @media print { body { padding:15px 20px; } }
</style></head><body>
<div class="header">
  <div class="logo">\u{1F331}</div>
  <div class="empresa-info">
    <div class="empresa-nome">Agronotas</div>
    <div class="empresa-detalhe">CGCOM — Gestão de Notas Fiscais</div>
    <div class="empresa-detalhe">Ambiente de Homologação</div>
  </div>
  <div class="pedido-num">
    <h2>PEDIDO #${pedido.id?.toString().slice(-4)}</h2>
    <span>Data: ${fmtDate(pedido.data_criacao)}</span><br/>
    <span>Status: ${pedido.status || "—"}</span>
  </div>
</div>

<div class="section">
  <div class="section-title">\u{1F464} Dados do Cliente</div>
  <div class="cliente-grid">
    <div class="campo"><b>Nome:</b> ${cli?.nome || "—"}</div>
    <div class="campo"><b>CPF/CNPJ:</b> ${cli?.cnpj_cpf || "—"}</div>
    <div class="campo"><b>IE/RG:</b> ${cli?.ies_rg || "—"}</div>
    <div class="campo"><b>Telefone:</b> ${cli?.fone || cli?.celular || "—"}</div>
    <div class="campo"><b>Endereço:</b> ${[cli?.logradouro, cli?.numero, cli?.bairro, cli?.cep].filter(Boolean).join(", ") || "—"}</div>
    <div class="campo"><b>E-mail:</b> ${cli?.email || "—"}</div>
  </div>
</div>

<div class="section">
  <div class="section-title">\u{1F4E6} Itens do Pedido</div>
  <table>
    <thead><tr><th>#</th><th>Descrição</th><th class="text-right">Qtd</th><th class="text-right">Vl. Unit.</th><th class="text-right">Vl. Total</th></tr></thead>
    <tbody>
      ${itens.map((item, i) => `<tr><td>${i + 1}</td><td>${item.descricao || "—"}</td><td class="text-right">${Number(item.quantidade || 0)}</td><td class="text-right">${Number(item.valor_unitario || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}</td><td class="text-right">${Number(item.valor_total || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}</td></tr>`).join("")}
      <tr class="total-row"><td colspan="4" class="text-right">TOTAL</td><td class="text-right">${Number(pedido.valor_total || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}</td></tr>
    </tbody>
  </table>
</div>

<div class="footer">
  <div class="section">
    <div class="section-title">\u{1F4B3} Forma de Pagamento</div>
    <div class="campo" style="font-size:14px;font-weight:600">${pgto?.descricao || "Não informada"}</div>
  </div>
  <div class="section">
    <div class="section-title">\u{1F4DD} Observações</div>
    <div class="obs-text">${pedido.observacao || "Nenhuma observação."}</div>
  </div>
</div>

<div class="print-footer">Agronotas — Documento gerado em ${new Date().toLocaleString("pt-BR")} — CGCOM</div>
</body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 400);
  };

  if (selected) {
    const cli = getCliente(selected.cliente_id);
    return (
      <div className="content">
        <div className="detail-hero">
          <div className="detail-hero-icon">🛒</div>
          <div className="detail-hero-name">Pedido #{selected.id?.toString().slice(-4)}</div>
          <div className="detail-hero-sub">{fmtDate(selected.data_criacao)}</div>
        </div>
        <div className="detail-body">
          <div className="action-row">
            <button className="btn btn-primary btn-sm" style={{ flex: 1 }} onClick={() => printPedido(selected)}>🖨️ Imprimir</button>
            <button className="btn btn-danger btn-sm" style={{ flex: 1 }} onClick={() => handleDelete(selected.id)}>🗑️ Excluir</button>
          </div>
          <div className="info-group">
            {[
              ["Cliente", cli?.nome || `ID ${selected.cliente_id}`],
              ["Status", selected.status],
              ["Total", fmt(selected.valor_total)],
              ["Data", fmtDate(selected.data_criacao)],
              ["Observação", selected.observacao || "—"],
            ].map(([l, v]) => (
              <div key={l} className="info-row">
                <span className="info-label">{l}</span>
                <span className="info-value">{v}</span>
              </div>
            ))}
          </div>
          <div className="info-group">
            <div className="form-section-title">📦 Itens</div>
            {(selected.itens || []).map((item, i) => (
              <div key={i} className="info-row">
                <span className="info-label">{item.descricao}</span>
                <span className="info-value">{item.quantidade}x {fmt(item.valor_unitario)}</span>
              </div>
            ))}
            <div className="info-row" style={{ marginTop: 4 }}>
              <span className="info-label" style={{ fontWeight: 800 }}>TOTAL</span>
              <span className="info-value" style={{ color: "var(--g700)", fontWeight: 800 }}>{fmt(selected.valor_total)}</span>
            </div>
          </div>
          <button className="btn btn-ghost mt12" onClick={() => setSelected(null)}>← Voltar</button>
        </div>
      </div>
    );
  }

  return (
    <div className="content list-screen">
      {items.length === 0 ? <Empty icon="🛒" title="Nenhum pedido" desc="Toque em + para criar" /> : (
        <div className="list-items">
          {items.map(p => {
            const cli = getCliente(p.cliente_id);
            return (
              <div key={p.id} className="list-item" onClick={() => setSelected(p)}>
                <div className="list-avatar" style={{ background: "var(--amber100)", color: "var(--amber400)" }}>🛒</div>
                <div className="list-info">
                  <div className="list-name">{cli?.nome || `Cliente #${p.cliente_id}`}</div>
                  <div className="list-sub">{fmtDate(p.data_criacao)} • {fmt(p.valor_total)}</div>
                </div>
                <span className={`pedido-status ${p.status}`}>{p.status}</span>
                <span className="list-arrow">›</span>
              </div>
            );
          })}
        </div>
      )}
      <button className="fab" onClick={openNew}>+</button>
      <Modal open={showForm} onClose={() => setShowForm(false)} title="➕ Novo Pedido"
        actions={[
          <button key="s" className="btn btn-primary" onClick={handleSave}>💾 Salvar Pedido</button>,
          <button key="c" className="btn btn-ghost" onClick={() => setShowForm(false)}>Cancelar</button>
        ]}>
        <Field label="Cliente *">
          <select value={formData.cliente_id || ""} onChange={e => setFormData({ ...formData, cliente_id: e.target.value })}>
            <option value="">Selecione o cliente</option>
            {clientes.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </select>
        </Field>
        <div className="field-row">
          <Field label="Forma de Pgto">
            <select value={formData.forma_pagamento_id || ""} onChange={e => setFormData({ ...formData, forma_pagamento_id: e.target.value })}>
              <option value="">Selecione</option>
              {formasPgto.map(f => <option key={f.id} value={f.id}>{f.descricao}</option>)}
            </select>
          </Field>
          <Field label="Status">
            <select value={formData.status || "ABERTO"} onChange={e => setFormData({ ...formData, status: e.target.value })}>
              <option value="ABERTO">Aberto</option>
              <option value="FATURADO">Faturado</option>
              <option value="CANCELADO">Cancelado</option>
            </select>
          </Field>
        </div>
        <Field label="Observação"><textarea value={formData.observacao || ""} placeholder="Observações do pedido..." onChange={e => setFormData({ ...formData, observacao: e.target.value })} /></Field>
        <div className="field" style={{ marginTop: 4 }}>
          <div className="product-picker">
            <div className="product-picker-header">
              <span className="product-picker-title">📦 Produtos ({itensPedido.length})</span>
              <button className="product-picker-add" onClick={() => setShowPicker(true)}>+ Adicionar</button>
            </div>
            {itensPedido.length === 0 && <div style={{ padding: "16px", textAlign: "center", color: "var(--slate400)", fontSize: 13 }}>Nenhum produto adicionado</div>}
            {itensPedido.map((item, idx) => (
              <div key={idx} className="product-item">
                <div style={{ flex: 1 }}>
                  <div className="product-item-name">{item.descricao}</div>
                  <div style={{ fontSize: 11, color: "var(--slate400)", marginTop: 2 }}>{fmt(item.valor_unitario)} × {item.quantidade}</div>
                </div>
                <span className="product-price">{fmt(item.valor_total)}</span>
                <span className="product-remove" onClick={() => removeItem(idx)}>×</span>
              </div>
            ))}
            {itensPedido.length > 0 && (
              <div style={{ padding: "10px 12px", background: "var(--g50)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: "var(--slate600)" }}>TOTAL</span>
                <span style={{ fontSize: 16, fontWeight: 800, color: "var(--g700)" }}>{fmt(totalPedido)}</span>
              </div>
            )}
          </div>
        </div>
      </Modal>
      <ProductPickerModal
        open={showPicker}
        onClose={() => setShowPicker(false)}
        produtos={produtos}
        onConfirm={addItem}
      />
    </div>
  );
}

// ---------- NOTAS FISCAIS ----------
function NFEScreen({ toast }) {
  const fetchFn = useCallback(() => ApiModel.nfe.listar(), []);
  const { items, loading, reload } = useList(fetchFn);
  const [clientes, setClientes] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [formasPgto] = useState(FormasPgtoModel.listar());
  const [operacoes] = useState(OperacoesModel.listar());
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({});
  const [itensNF, setItensNF] = useState([]);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);
  const [emitindo, setEmitindo] = useState(false);
  const [clienteSelecionado, setClienteSelecionado] = useState(null);
  const [showPicker, setShowPicker] = useState(false);

  useEffect(() => {
    ApiModel.pessoas.listar().then(setClientes).catch(() => {});
    ApiModel.produtos.listar().then(setProdutos).catch(() => {});
  }, []);

  // Ao selecionar cliente, preenche cpf_consumidor e nome_consumidor
  // REGRA: apenas Pessoa Física (tipo_inscricao === "F") envia esses campos;
  // Pessoa Jurídica envia null em ambos.
  const handleClienteChange = (clienteId) => {
    const cli = clientes.find(c => c.id === Number(clienteId));
    setClienteSelecionado(cli || null);
    const isPF = cli?.tipo_inscricao === "F";
    setFormData(prev => ({
      ...prev,
      cliente_id: clienteId,
      cpf_consumidor: isPF ? (cli?.cnpj_cpf || null) : null,
      nome_consumidor: isPF ? (cli?.nome || null) : null,
    }));
  };

  const openNew = () => {
    setFormData({ ambiente: "HOMOLOGACAO", modelo: "NFE", valor_desconto: 0 });
    setItensNF([]);
    setClienteSelecionado(null);
    setShowForm(true);
  };

  const addItemNF = (item) => {
    const numItem = itensNF.length + 1;
    setItensNF(prev => [...prev, { ...item, numero_item: numItem, valor_desconto: 0 }]);
  };

  const updateItemNF = (idx, field, value) => {
    setItensNF(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const updated = { ...item, [field]: value };
      if (field === "produto_id") {
        const p = produtos.find(p => p.id === Number(value));
        if (p) { updated.descricao = p.descricao; updated.valor_unitario = Number(p.preco_venda || 0); }
      }
      updated.valor_total = (Number(updated.quantidade || 0) * Number(updated.valor_unitario || 0)) - Number(updated.valor_desconto || 0);
      return updated;
    }));
  };

  const removeItemNF = (idx) => setItensNF(prev => prev.filter((_, i) => i !== idx).map((item, i) => ({ ...item, numero_item: i + 1 })));

  const totalProdutos = itensNF.reduce((s, i) => s + Number(i.valor_total || 0), 0);
  const totalFinal = totalProdutos - Number(formData.valor_desconto || 0);

  const handleSave = async () => {
    if (!formData.cliente_id) return toast.show("Selecione o cliente", "error");
    if (!formData.operacao_fiscal_id) return toast.show("Selecione a operação fiscal", "error");
    if (!formData.forma_pagamento_id) return toast.show("Selecione a forma de pagamento", "error");
    if (!itensNF.length) return toast.show("Adicione pelo menos 1 produto", "error");
    setSaving(true);
    try {
      // Regra: CPF/nome do consumidor somente para Pessoa Física (tipo_inscricao === "F")
      const cliAtual = clientes.find(c => c.id === Number(formData.cliente_id));
      const isPF = cliAtual?.tipo_inscricao === "F";

      const payload = {
        ...formData,
        cliente_id: Number(formData.cliente_id),
        operacao_fiscal_id: Number(formData.operacao_fiscal_id),
        forma_pagamento_id: Number(formData.forma_pagamento_id),
        cpf_consumidor: isPF ? (formData.cpf_consumidor || cliAtual?.cnpj_cpf || null) : null,
        nome_consumidor: isPF ? (cliAtual?.nome || null) : null,
        valor_total_produtos: totalProdutos,
        valor_total: totalFinal,
        valor_desconto: Number(formData.valor_desconto || 0),
        listaNfeDetalhe: itensNF.map(item => ({
          numero_item: item.numero_item,
          produto_id: Number(item.produto_id),
          quantidade: Number(item.quantidade),
          valor_unitario: Number(item.valor_unitario),
          valor_desconto: Number(item.valor_desconto || 0),
          valor_total: Number(item.valor_total),
        })),
      };
      if (formData.id) await ApiModel.nfe.alterar(payload);
      else await ApiModel.nfe.cadastrar(payload);
      toast.show(formData.id ? "NF-e atualizada!" : "NF-e criada!", "success");
      setShowForm(false); reload();
    } catch (e) { toast.show(e.message, "error"); }
    finally { setSaving(false); }
  };

  const handleEmitir = async (id) => {
    setEmitindo(true);
    try {
      const result = await ApiModel.nfe.emitir(id);
      toast.show(`NF-e emitida! Status: ${result.status_nota}`, "success");
      setSelected(result); reload();
    } catch (e) { toast.show(e.message, "error"); }
    finally { setEmitindo(false); }
  };

  const handleDelete = async (id) => {
    try {
      await ApiModel.nfe.excluir(id);
      toast.show("NF-e excluída", "success");
      setSelected(null); reload();
    } catch (e) { toast.show(e.message, "error"); }
  };

  const getCliente = (id) => clientes.find(c => c.id === Number(id));

  if (selected) {
    const cli = getCliente(selected.cliente_id);
    const status = selected.status_nota || "RASCUNHO";
    return (
      <div className="content">
        <div className="detail-hero">
          <div className="detail-hero-icon">🧾</div>
          <div className="detail-hero-name">NF-e #{selected.numero || "—"}</div>
          <div className="detail-hero-sub">Série {selected.serie || "—"} • {fmtDate(selected.data_hora_emissao)}</div>
        </div>
        <div className="detail-body">
          <div className="action-row">
            {status !== "AUTORIZADA" && (
              <button className="btn btn-primary btn-sm" style={{ flex: 1 }} disabled={emitindo} onClick={() => handleEmitir(selected.id)}>
                {emitindo ? "Emitindo..." : "⚡ Emitir"}
              </button>
            )}
            {status === "RASCUNHO" && (
              <button className="btn btn-danger btn-sm" style={{ flex: 1 }} onClick={() => handleDelete(selected.id)}>🗑️</button>
            )}
          </div>
          <div className="info-group">
            <div className="info-row">
              <span className="info-label">Status</span>
              <span className={`nf-status ${status}`}>{status}</span>
            </div>
            {[
              ["Cliente", cli?.nome || `ID ${selected.cliente_id}`],
              ["Chave Acesso", selected.chave_acesso ? selected.chave_acesso.slice(0, 22) + "..." : "—"],
              ["Ambiente", selected.ambiente],
              ["Modelo", selected.modelo],
              ["Total Produtos", fmt(selected.valor_total_produtos)],
              ["Desconto", fmt(selected.valor_desconto)],
              ["TOTAL", fmt(selected.valor_total)],
            ].map(([l, v]) => (
              <div key={l} className="info-row">
                <span className="info-label">{l}</span>
                <span className="info-value">{v}</span>
              </div>
            ))}
          </div>
          {selected.rejeicao_sefaz && (
            <div style={{ background: "var(--red100)", color: "var(--red500)", borderRadius: 12, padding: 12, fontSize: 13, marginBottom: 12 }}>
              ⚠️ <strong>Rejeição SEFAZ:</strong> {selected.rejeicao_sefaz}
            </div>
          )}
          <div className="info-group">
            <div className="form-section-title">📦 Itens</div>
            {(selected.listaNfeDetalhe || []).map((item, i) => {
              const prod = produtos.find(p => p.id === item.produto_id);
              return (
                <div key={i} className="info-row">
                  <span className="info-label">{prod?.descricao || `Prod. ${item.produto_id}`}</span>
                  <span className="info-value">{item.quantidade}x {fmt(item.valor_unitario)}</span>
                </div>
              );
            })}
          </div>
          <button className="btn btn-ghost mt12" onClick={() => setSelected(null)}>← Voltar</button>
        </div>
      </div>
    );
  }

  return (
    <div className="content list-screen">
      {loading ? <Loading /> : items.length === 0 ? <Empty icon="🧾" title="Nenhuma NF-e" desc="Toque em + para emitir" /> : (
        <div className="list-items">
          {items.map(nf => {
            const cli = getCliente(nf.cliente_id);
            const status = nf.status_nota || "RASCUNHO";
            return (
              <div key={nf.id} className="list-item" onClick={() => setSelected(nf)}>
                <div className="list-avatar" style={{ background: "#f3e8ff", color: "#9333ea" }}>🧾</div>
                <div className="list-info">
                  <div className="list-name">{cli?.nome || `Cliente #${nf.cliente_id}`}</div>
                  <div className="list-sub">NF #{nf.numero || "—"} • {fmt(nf.valor_total)}</div>
                </div>
                <span className={`nf-status ${status}`}>{status}</span>
                <span className="list-arrow">›</span>
              </div>
            );
          })}
        </div>
      )}
      <button className="fab" onClick={openNew}>+</button>
      <Modal open={showForm} onClose={() => setShowForm(false)} title="➕ Nova NF-e"
        actions={[
          <button key="s" className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? "Salvando..." : "💾 Salvar NF-e"}</button>,
          <button key="c" className="btn btn-ghost" onClick={() => setShowForm(false)}>Cancelar</button>
        ]}>
        <Field label="Cliente *">
          <select value={formData.cliente_id || ""} onChange={e => handleClienteChange(e.target.value)}>
            <option value="">Selecione o cliente</option>
            {clientes.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </select>
        </Field>
        {clienteSelecionado && (() => {
          const isPF = clienteSelecionado.tipo_inscricao === "F";
          return (
            <div style={{ borderRadius: 10, padding: "10px 12px", marginBottom: 12, border: `1.5px solid ${isPF ? "var(--g200)" : "var(--slate200)"}`, background: isPF ? "var(--g50)" : "var(--slate50)" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: isPF ? "var(--g700)" : "var(--slate600)" }}>
                  {isPF ? "👤 Pessoa Física" : "🏢 Pessoa Jurídica"}
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: isPF ? "var(--g200)" : "var(--slate200)", color: isPF ? "var(--g800)" : "var(--slate600)" }}>
                  {isPF ? "CPF e Nome serão enviados" : "CPF e Nome: null (PJ)"}
                </span>
              </div>
              <div style={{ fontSize: 12, color: "var(--slate600)", lineHeight: 1.8 }}>
                <strong>Nome:</strong> {clienteSelecionado.nome}<br />
                <strong>{isPF ? "CPF" : "CNPJ"}:</strong> {clienteSelecionado.cnpj_cpf || "—"}
                {!isPF && (
                  <div style={{ marginTop: 4, fontSize: 11, color: "var(--slate400)", fontStyle: "italic" }}>
                    ℹ️ cpf_consumidor e nome_consumidor serão enviados como <strong>null</strong> por se tratar de pessoa jurídica.
                  </div>
                )}
                {isPF && (
                  <div style={{ marginTop: 4, fontSize: 11, color: "var(--g700)", fontStyle: "italic" }}>
                    ✅ CPF e nome serão incluídos automaticamente no payload da NF-e.
                  </div>
                )}
              </div>
            </div>
          );
        })()}
        <div className="field-row">
          <Field label="Operação Fiscal *">
            <select value={formData.operacao_fiscal_id || ""} onChange={e => setFormData({ ...formData, operacao_fiscal_id: e.target.value })}>
              <option value="">Selecione</option>
              {operacoes.map(o => <option key={o.id} value={o.id}>{o.descricao}</option>)}
            </select>
          </Field>
          <Field label="Forma de Pgto *">
            <select value={formData.forma_pagamento_id || ""} onChange={e => setFormData({ ...formData, forma_pagamento_id: e.target.value })}>
              <option value="">Selecione</option>
              {formasPgto.map(f => <option key={f.id} value={f.id}>{f.descricao}</option>)}
            </select>
          </Field>
        </div>
        <div className="field-row">
          <Field label="Ambiente">
            <select value={formData.ambiente || "HOMOLOGACAO"} onChange={e => setFormData({ ...formData, ambiente: e.target.value })}>
              <option value="HOMOLOGACAO">Homologação</option>
              <option value="PRODUCAO">Produção</option>
            </select>
          </Field>
          <Field label="Modelo">
            <select value={formData.modelo || "NFE"} onChange={e => setFormData({ ...formData, modelo: e.target.value })}>
              <option value="NFE">NF-e</option>
              <option value="NFCE">NFC-e</option>
            </select>
          </Field>
        </div>
        <div className="field" style={{ marginTop: 4 }}>
          <div className="product-picker">
            <div className="product-picker-header">
              <span className="product-picker-title">📦 Produtos ({itensNF.length})</span>
              <button className="product-picker-add" onClick={() => setShowPicker(true)}>+ Adicionar</button>
            </div>
            {itensNF.length === 0 && <div style={{ padding: "14px", textAlign: "center", color: "var(--slate400)", fontSize: 13 }}>Nenhum produto</div>}
            {itensNF.map((item, idx) => (
              <div key={idx} className="product-item">
                <div style={{ flex: 1 }}>
                  <div className="product-item-name">{item.descricao}</div>
                  <div style={{ fontSize: 11, color: "var(--slate400)", marginTop: 2 }}>{fmt(item.valor_unitario)} × {item.quantidade}</div>
                </div>
                <span className="product-price">{fmt(item.valor_total)}</span>
                <span className="product-remove" onClick={() => removeItemNF(idx)}>×</span>
              </div>
            ))}
            {itensNF.length > 0 && (
              <div style={{ padding: "10px 12px", background: "var(--g50)", display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: "var(--slate600)" }}>SUBTOTAL</span>
                <span style={{ fontSize: 15, fontWeight: 800, color: "var(--g700)" }}>{fmt(totalProdutos)}</span>
              </div>
            )}
          </div>
        </div>
        <div className="field-row mt8">
          <Field label="Desconto">
            <input type="number" step="0.01" value={formData.valor_desconto || ""} placeholder="0,00" onChange={e => setFormData({ ...formData, valor_desconto: e.target.value })} />
          </Field>
          <Field label="TOTAL NF-e">
            <input readOnly value={fmt(totalFinal)} style={{ background: "var(--g50)", color: "var(--g700)", fontWeight: 700 }} />
          </Field>
        </div>
      </Modal>
      <ProductPickerModal
        open={showPicker}
        onClose={() => setShowPicker(false)}
        produtos={produtos}
        onConfirm={addItemNF}
      />
    </div>
  );
}

// ---------- PROFILE ----------
function ProfileScreen({ onLogout, toast, appCfg, onSaveConfig, isDevUser, currentUser }) {
  const [cfg, setCfg] = useState({ ...appCfg });
  const [editing, setEditing] = useState(false);
  const [confirmAmbiente, setConfirmAmbiente] = useState(false);
  const [pendingAmbiente, setPendingAmbiente] = useState(null);

  const isHml = cfg.ambiente === "HOMOLOGACAO";

  const handleAmbienteChange = (newAmbiente) => {
    if (!isDevUser && newAmbiente === "HOMOLOGACAO") {
      toast.show("Apenas o usuário CGCOM pode ativar Homologação", "error");
      return;
    }
    if (newAmbiente === "PRODUCAO") {
      // Confirmar antes de ir para Produção
      setPendingAmbiente("PRODUCAO");
      setConfirmAmbiente(true);
    } else {
      setCfg(prev => ({ ...prev, ambiente: newAmbiente, apiBase: API_URLS[newAmbiente] }));
    }
  };

  const confirmSwitch = () => {
    setCfg(prev => ({ ...prev, ambiente: pendingAmbiente, apiBase: API_URLS[pendingAmbiente] }));
    setConfirmAmbiente(false);
    setPendingAmbiente(null);
  };

  const handleApiBaseChange = (url) => {
    if (!isDevUser) { toast.show("Apenas o usuário CGCOM pode alterar a URL da API", "error"); return; }
    setCfg(prev => ({ ...prev, apiBase: url }));
  };

  const handleSave = () => {
    onSaveConfig(cfg);
    setEditing(false);
  };

  const handleCancel = () => {
    setCfg({ ...appCfg });
    setEditing(false);
  };

  return (
    <div className="content profile-screen">
      <div className="profile-hero">
        <div className="profile-avatar">🌱</div>
        <div className="profile-name">Agronotas</div>
        <div className="profile-role">{currentUser} • Gestão de NF-e</div>
      </div>

      <div className="profile-body">

        {/* ── Ambiente & API ── */}
        <div style={{ background: "white", borderRadius: 16, padding: "18px 16px", marginBottom: 12, boxShadow: "var(--shadow)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: isHml ? "var(--amber-500, #f59e0b)" : "var(--g700)", textTransform: "uppercase", letterSpacing: ".6px" }}>
              {isHml ? "⚠️ Homologação" : "🟢 Produção"}
            </div>
            {!editing ? (
              <button
                className="btn btn-secondary btn-sm"
                style={{ width: "auto" }}
                onClick={() => setEditing(true)}
              >
                ✏️ Editar
              </button>
            ) : (
              <div style={{ display: "flex", gap: 6 }}>
                <button className="btn btn-primary btn-sm" style={{ width: "auto" }} onClick={handleSave}>💾 Salvar</button>
                <button className="btn btn-ghost btn-sm" style={{ width: "auto" }} onClick={handleCancel}>✕</button>
              </div>
            )}
          </div>

          {/* Ambiente selector */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--slate600)", textTransform: "uppercase", letterSpacing: ".4px", marginBottom: 8 }}>
              Ambiente de Emissão
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              {["PRODUCAO", "HOMOLOGACAO"].map(amb => {
                const isSelected = cfg.ambiente === amb;
                const isHmlOption = amb === "HOMOLOGACAO";
                const blocked = isHmlOption && !isDevUser;
                return (
                  <button
                    key={amb}
                    onClick={() => editing && handleAmbienteChange(amb)}
                    disabled={!editing}
                    style={{
                      flex: 1, padding: "10px 8px", borderRadius: 10,
                      border: isSelected ? `2px solid ${isHmlOption ? "#f59e0b" : "var(--g600)"}` : "2px solid var(--slate200)",
                      background: isSelected ? (isHmlOption ? "#fef3c7" : "var(--g50)") : "var(--slate50)",
                      cursor: editing ? (blocked ? "not-allowed" : "pointer") : "default",
                      opacity: editing ? (blocked ? 0.45 : 1) : 0.75,
                      transition: "all .18s",
                      fontFamily: "inherit",
                    }}
                  >
                    <div style={{ fontSize: 18, marginBottom: 4 }}>{isHmlOption ? "🧪" : "🏭"}</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: isSelected ? (isHmlOption ? "#92400e" : "var(--g800)") : "var(--slate500)" }}>
                      {amb === "PRODUCAO" ? "Produção" : "Homologação"}
                    </div>
                    {blocked && (
                      <div style={{ fontSize: 10, color: "var(--slate400)", marginTop: 2 }}>🔒 restrito</div>
                    )}
                  </button>
                );
              })}
            </div>
            {isHml && (
              <div style={{ marginTop: 8, background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 8, padding: "8px 10px", fontSize: 12, color: "#92400e", fontWeight: 500 }}>
                ⚠️ Ambiente de testes — NF-e não tem validade fiscal.
              </div>
            )}
          </div>

          {/* URL da API */}
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--slate600)", textTransform: "uppercase", letterSpacing: ".4px" }}>
                URL da API
              </div>
              {!isDevUser && (
                <span style={{ fontSize: 10, color: "var(--slate400)", fontWeight: 600 }}>🔒 somente CGCOM</span>
              )}
            </div>
            <input
              value={cfg.apiBase}
              onChange={e => handleApiBaseChange(e.target.value)}
              readOnly={!editing || !isDevUser}
              style={{
                width: "100%", padding: "10px 12px",
                border: `1.5px solid ${editing && isDevUser ? "var(--g500)" : "var(--slate200)"}`,
                borderRadius: 10,
                fontFamily: "JetBrains Mono, monospace",
                fontSize: 12, color: "var(--slate700)",
                background: editing && isDevUser ? "white" : "var(--slate100)",
                outline: "none",
                opacity: !isDevUser ? 0.7 : 1,
              }}
            />
            <div style={{ marginTop: 6, display: "flex", gap: 6 }}>
              {isDevUser && editing && (
                <>
                  <button
                    style={{ flex: 1, padding: "6px 8px", borderRadius: 8, border: "1.5px solid var(--g200)", background: cfg.apiBase === API_URLS.PRODUCAO ? "var(--g100)" : "var(--slate50)", fontSize: 11, fontWeight: 700, cursor: "pointer", color: "var(--g700)", fontFamily: "inherit" }}
                    onClick={() => setCfg(prev => ({ ...prev, apiBase: API_URLS.PRODUCAO, ambiente: "PRODUCAO" }))}
                  >
                    🏭 Usar URL Produção
                  </button>
                  <button
                    style={{ flex: 1, padding: "6px 8px", borderRadius: 8, border: "1.5px solid #fcd34d", background: cfg.apiBase === API_URLS.HOMOLOGACAO ? "#fef3c7" : "var(--slate50)", fontSize: 11, fontWeight: 700, cursor: "pointer", color: "#92400e", fontFamily: "inherit" }}
                    onClick={() => setCfg(prev => ({ ...prev, apiBase: API_URLS.HOMOLOGACAO, ambiente: "HOMOLOGACAO" }))}
                  >
                    🧪 Usar URL HML
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* ── Info do sistema ── */}
        <div className="menu-list">
          <div className="menu-list-item">
            <div className="menu-list-icon" style={{ background: "var(--g100)" }}>🏢</div>
            <span className="menu-list-label">Empresa ID</span>
            <span style={{ fontSize: 13, color: "var(--slate400)", fontWeight: 600 }}>{EMPRESA_ID}</span>
          </div>
          <div className="menu-list-item">
            <div className="menu-list-icon" style={{ background: "var(--blue100)" }}>👤</div>
            <span className="menu-list-label">Usuário logado</span>
            <span style={{ fontSize: 13, color: isDevUser ? "var(--g700)" : "var(--slate400)", fontWeight: 700 }}>
              {currentUser} {isDevUser ? "🔓" : ""}
            </span>
          </div>
          <div className="menu-list-item" onClick={() => toast.show("Versão 1.0.0 — Agronotas", "info")}>
            <div className="menu-list-icon" style={{ background: "var(--slate100)" }}>ℹ️</div>
            <span className="menu-list-label">Versão do App</span>
            <span style={{ fontSize: 13, color: "var(--slate400)", fontWeight: 600 }}>1.0.0</span>
          </div>
        </div>

        <button className="btn btn-danger mt12" onClick={onLogout}>🚪 Sair da conta</button>
        <div style={{ textAlign: "center", marginTop: 20, fontSize: 12, color: "var(--slate300)" }}>
          Agronotas © 2024 • Powered by CGCOM
        </div>
      </div>

      {/* ── Modal confirmação troca para PRODUÇÃO ── */}
      <Modal
        open={confirmAmbiente}
        onClose={() => { setConfirmAmbiente(false); setPendingAmbiente(null); }}
        title="⚠️ Trocar para Produção"
        actions={[
          <button key="ok" className="btn btn-primary" onClick={confirmSwitch}>✅ Confirmar — ir para Produção</button>,
          <button key="cancel" className="btn btn-ghost" onClick={() => { setConfirmAmbiente(false); setPendingAmbiente(null); }}>Cancelar</button>,
        ]}
      >
        <p style={{ fontSize: 14, color: "var(--slate600)", lineHeight: 1.6 }}>
          Você está prestes a mudar para o ambiente de <strong>Produção</strong>.<br /><br />
          As NF-e emitidas terão <strong>validade fiscal real</strong> junto à SEFAZ. Confirma a mudança?
        </p>
      </Modal>
    </div>
  );
}

// ============================================================
// APP ROOT — CONTROLLER
// ============================================================
const NAV_ITEMS = [
  { key: "dashboard", icon: "🏠", label: "Início" },
  { key: "clientes", icon: "👥", label: "Clientes" },
  { key: "nfe", icon: "🧾", label: "NF-e" },
  { key: "pedidos", icon: "🛒", label: "Pedidos" },
  { key: "perfil", icon: "⚙️", label: "Config" },
];

const SCREEN_TITLES = {
  dashboard: { title: "Agronotas", subtitle: "Gestão de NF-e" },
  clientes: { title: "Clientes", subtitle: "Gerenciar pessoas" },
  produtos: { title: "Produtos", subtitle: "Estoque e preços" },
  pedidos: { title: "Pedidos", subtitle: "Controle de pedidos" },
  nfe: { title: "Notas Fiscais", subtitle: "NF-e" },
  formas_pgto: { title: "Formas de Pagamento", subtitle: "Meios de pagamento" },
  operacoes: { title: "Operações Fiscais", subtitle: "Configurações fiscais" },
  perfil: { title: "Configurações", subtitle: "Conta e sistema" },
};

export default function App() {
  const [logged, setLogged] = useState(false);
  const [screen, setScreen] = useState("dashboard");
  const [currentUser, setCurrentUser] = useState("");
  const [appCfg, setAppCfg] = useState(() => AppConfig.load());
  const toast = useToast();

  // Sync ApiModel base URL whenever config changes
  useEffect(() => {
    ApiModel._baseUrl = appCfg.apiBase;
  }, [appCfg]);

  // Inject styles once
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = STYLES;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const handleLogin = (username) => {
    setCurrentUser(username);
    setLogged(true);
  };

  if (!logged) return <div className="shell"><LoginScreen onLogin={handleLogin} /></div>;

  const isDevUser = currentUser === DEVUSER;

  const handleSaveConfig = (newCfg) => {
    AppConfig.save(newCfg);
    setAppCfg(newCfg);
    ApiModel._baseUrl = newCfg.apiBase;
    toast.show("Configurações salvas!", "success");
  };

  const inNav = NAV_ITEMS.some(n => n.key === screen);
  const canGoBack = !inNav;
  const info = SCREEN_TITLES[screen] || { title: screen, subtitle: "" };

  const handleLogout = () => { ApiModel.setToken(null); setLogged(false); setCurrentUser(""); setScreen("dashboard"); };

  const renderScreen = () => {
    switch (screen) {
      case "dashboard": return <DashboardScreen onNavigate={setScreen} />;
      case "clientes": return <ClientesScreen toast={toast} />;
      case "produtos": return <ProdutosScreen toast={toast} />;
      case "pedidos": return <PedidosScreen toast={toast} />;
      case "nfe": return <NFEScreen toast={toast} appCfg={appCfg} />;
      case "formas_pgto": return <FormasPgtoScreen toast={toast} />;
      case "operacoes": return <OperacoesScreen toast={toast} />;
      case "perfil": return <ProfileScreen onLogout={handleLogout} toast={toast} appCfg={appCfg} onSaveConfig={handleSaveConfig} isDevUser={isDevUser} currentUser={currentUser} />;
      default: return <DashboardScreen onNavigate={setScreen} />;
    }
  };

  return (
    <div className="shell">
      {/* Top Bar */}
      <div className="topbar">
        <div className="topbar-inner">
          <div className="topbar-left">
            {canGoBack && (
              <button className="topbar-back" onClick={() => setScreen("dashboard")}>‹</button>
            )}
            <div>
              <div className="topbar-title">{info.title}</div>
              {info.subtitle && <div className="topbar-subtitle">{info.subtitle}</div>}
            </div>
          </div>
          {screen === "dashboard" && (
            <button className="topbar-action" onClick={() => setScreen("perfil")}>⚙️</button>
          )}
        </div>
      </div>

      {/* Screen Content */}
      {renderScreen()}

      {/* Bottom Navigation */}
      <nav className="bottom-nav">
        {NAV_ITEMS.map(item => (
          <button
            key={item.key}
            className={`nav-item ${screen === item.key ? "active" : ""}`}
            onClick={() => setScreen(item.key)}
          >
            <span className="nav-icon">{item.icon}</span>
            <span className="nav-label">{item.label}</span>
          </button>
        ))}
      </nav>

      {/* Toast */}
      <Toast toast={toast.toast} />
    </div>
  );
}
